<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import ConfirmModal from "../components/ConfirmModal.vue";
import StatusModal from "../components/StatusModal.vue";
import ChatAssistant from "../components/ChatAssistant.vue";
import { listRecipes, createRecipe, updateRecipe, deleteRecipe, getRecipeIngredients, getRecipeIngredientsSafe, getRecipeIngredientById, addRecipeIngredient, updateRecipeIngredient, deleteRecipeIngredient, listIngredientUsage, createIngredientUsage, listIngredientImages, createIngredientImage, updateIngredientImage, deleteIngredientImage, getIngredientStockByNames, getInitialStockByNames, getStockStatus, subscribeToStockAlerts, getAllIngredientStocks, adjustIngredientStock } from "../db/ingredients.js";
import { currentUser } from "../db/auth.js";
import { supabase } from "../db/supabaseClient.js";

const activeTab = ref("recipes"); // recipes | usage | images
const loading = ref(false);

// Custom modals and notifications
const showNotification = ref(false);
const notification = ref({ type: 'success', message: '' }); // type: success, error, warning

// CONFIRM MODAL
const showConfirmModal = ref(false);
const confirmModal = ref({ 
  title: '', 
  message: '', 
  html: '',
  onConfirm: () => {}, 
  onCancel: () => {},
  confirmText: 'Confirmar', 
  cancelText: 'Cancelar',
  type: 'warning'
});

// STATUS MODAL (Success/Error)
const showStatusModal = ref(false);
const statusModal = ref({ title: '', message: '', html: '', type: 'success' });

// Helper to replace Swal.fire confirmations
function confirmAction({ title, message, html, confirmText, cancelText, type }) {
  return new Promise((resolve) => {
    confirmModal.value = {
      title,
      message,
      html,
      confirmText: confirmText || 'Confirmar',
      cancelText: cancelText || 'Cancelar',
      type: type || 'warning',
      onConfirm: () => {
        showConfirmModal.value = false;
        resolve({ isConfirmed: true });
      },
      onCancel: () => {
        showConfirmModal.value = false;
        resolve({ isConfirmed: false });
      }
    };
    showConfirmModal.value = true;
  });
}

// Helper to replace Swal.fire alerts
function showAlert({ title, text, html, icon }) {
  statusModal.value = {
    title: title || (icon === 'error' ? 'Error' : 'Éxito'),
    message: text || '',
    html: html || '',
    type: icon === 'error' ? 'error' : 'success'
  };
  showStatusModal.value = true;
}

// RECIPES
const recipes = ref([]);
const showRecipeModal = ref(false);
const recipeForm = ref({ name: "", description: "", portions: 1, unit: "porciones", preparation_time: null, notes: "" });
const selectedRecipe = ref(null);
const recipeIngredients = ref([]);
const showIngredientsModal = ref(false);

// Pagination for Recipes
const recipePage = ref(1);
const recipeItemsPerPage = ref(10);

const paginatedRecipes = computed(() => {
  const start = (recipePage.value - 1) * recipeItemsPerPage.value;
  const end = start + recipeItemsPerPage.value;
  return filteredRecipes.value.slice(start, end);
});

const recipeTotalPages = computed(() => {
  return Math.ceil(filteredRecipes.value.length / recipeItemsPerPage.value);
});

function nextRecipePage() {
  if (recipePage.value < recipeTotalPages.value) {
    recipePage.value++;
  }
}

function prevRecipePage() {
  if (recipePage.value > 1) {
    recipePage.value--;
  }
}

const products = ref([]);
const ingredientForm = ref({ ingredient_name: "", product_id: null, quantity: 1, unit: "kg", notes: "" });
const generatingDescription = ref(false);
const stockAlertsChannel = ref(null);
const timers = ref({}); // { recipeId: remainingSeconds }
let timerInterval = null;

function calculateRemainingTime(recipe) {
  if (recipe.status_message) return 0;
  if (!recipe.timer_started_at || !recipe.preparation_time) return 0;
  
  const start = new Date(recipe.timer_started_at).getTime();
  const duration = recipe.preparation_time * 60 * 1000;
  const end = start + duration;
  const now = Date.now();
  const diff = Math.ceil((end - now) / 1000);
  return diff > 0 ? diff : 0;
}

function startGlobalTimer() {
  if (timerInterval) clearInterval(timerInterval);
  timerInterval = setInterval(() => {
    if (!recipes.value) return;
    recipes.value.forEach(recipe => {
      // Si ya tiene mensaje, no calcular
      if (recipe.status_message) {
        timers.value[recipe.id_recipes] = 0;
        return;
      }
      
      const remaining = calculateRemainingTime(recipe);
      timers.value[recipe.id_recipes] = remaining;
      
      // If finished and no message, update DB
      if (remaining <= 0 && !recipe.status_message && recipe.preparation_time > 0 && recipe.timer_started_at) {
        // Mark as finished locally to avoid multiple calls
        const msg = "Su platillo esta listo en unos momentos su mesero se lo lleva";
        recipe.status_message = msg;
        updateRecipe(recipe.id_recipes, { 
          status_message: msg 
        });
      }
    });
  }, 1000);
}

function formatDuration(minutes) {
  if (!minutes) return "-";
  const m = parseInt(minutes);
  if (isNaN(m)) return minutes;
  
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const min = m % 60;
  return min > 0 ? `${h} h ${min} min` : `${h} h`;
}

function formatTimer(seconds) {
  if (seconds <= 0) return "00:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

onUnmounted(() => {
  if (timerInterval) clearInterval(timerInterval);
});

// TIME COMPOSER STATE
const prepHour = ref("");
const prepMinute = ref("");

function setPrepFromForm() {
  const t = recipeForm.value.preparation_time;
  if (t) {
    // Si es número (minutos), convertir a HH:MM
    if (typeof t === "number") {
      const h = Math.floor(t / 60);
      const m = t % 60;
      prepHour.value = String(h).padStart(2, "0");
      prepMinute.value = String(m).padStart(2, "0");
      return;
    }
    // Si es string "HH:MM"
    if (typeof t === "string") {
      const [h, m] = String(t).split(":");
      prepHour.value = String(h || "").padStart(2, "0");
      prepMinute.value = String(m || "00").padStart(2, "0");
      return;
    }
  }
  
  prepHour.value = "";
  prepMinute.value = "";
}

function updatePrepTime() {
  if (prepHour.value !== "" && prepMinute.value !== "") {
    recipeForm.value.preparation_time = `${prepHour.value}:${prepMinute.value}`;
  } else {
    recipeForm.value.preparation_time = null;
  }
}

watch([prepHour, prepMinute], updatePrepTime);

// USAGE
const usageHistory = ref([]);
const usagePage = ref(1);
const usageItemsPerPage = 10;

const paginatedUsageHistory = computed(() => {
  const start = (usagePage.value - 1) * usageItemsPerPage;
  const end = start + usageItemsPerPage;
  return filteredUsageHistory.value.slice(start, end);
});

const usageTotalPages = computed(() => {
  return Math.ceil(filteredUsageHistory.value.length / usageItemsPerPage);
});

function nextUsagePage() {
  if (usagePage.value < usageTotalPages.value) {
    usagePage.value++;
  }
}

function prevUsagePage() {
  if (usagePage.value > 1) {
    usagePage.value--;
  }
}

const showUsageModal = ref(false);
const usageForm = ref({ recipe_id: null, portions_made: 1, notes: "", items: [] });
const usageItemForm = ref({ product_name: "", product_id: null, quantity: 1, unit: "kg" });

// IMAGES
const images = ref([]);
const showImageModal = ref(false);
const isEditingImage = ref(false);
const editingImageId = ref(null);
const imageForm = ref({ name: "", description: "", recipe_id: null, file: null, preview: null });

// Pagination for Images
const imagePage = ref(1);
const imageItemsPerPage = ref(20);

// FILTERS STATE
const filterRecipeSearch = ref("");
const filterRecipeStatus = ref("all");
const sortRecipeBy = ref("name-asc"); // name-asc, name-desc, time-asc, time-desc, portions-asc, portions-desc

const filterUsageSearch = ref("");
const filterUsageDateFrom = ref("");
const filterUsageDateTo = ref("");
const sortUsageBy = ref("date-desc"); // date-desc, date-asc, qty-desc, qty-asc

const filterImageSearch = ref("");
const sortImageBy = ref("name-asc"); // name-asc, name-desc

// RECIPE SEARCH SUGGESTIONS
const showRecipeSuggestions = ref(false);
const hoverRecipeIndex = ref(-1);

const recipeSuggestions = computed(() => {
  if (!filterRecipeSearch.value.trim()) {
    return recipes.value.slice(0, 8);
  }
  const q = filterRecipeSearch.value.toLowerCase();
  return recipes.value.filter(r => 
    (r.name || "").toLowerCase().includes(q)
  ).slice(0, 8);
});

function onRecipeSearchFocus() {
  showRecipeSuggestions.value = true;
}

function onRecipeSearchBlur() {
  setTimeout(() => {
    showRecipeSuggestions.value = false;
  }, 200);
}

function onRecipeSearchKeydown(e) {
  if (!showRecipeSuggestions.value || !recipeSuggestions.value.length) return;
  
  if (e.key === "ArrowDown") {
    e.preventDefault();
    hoverRecipeIndex.value = (hoverRecipeIndex.value + 1) % recipeSuggestions.value.length;
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    hoverRecipeIndex.value = (hoverRecipeIndex.value - 1 + recipeSuggestions.value.length) % recipeSuggestions.value.length;
  } else if (e.key === "Enter") {
    e.preventDefault();
    const recipe = recipeSuggestions.value[hoverRecipeIndex.value >= 0 ? hoverRecipeIndex.value : 0];
    if (recipe) selectRecipeSuggestion(recipe);
  } else if (e.key === "Escape") {
    showRecipeSuggestions.value = false;
  }
}

function selectRecipeSuggestion(recipe) {
  filterRecipeSearch.value = String(recipe.name).trim();
  showRecipeSuggestions.value = false;
}

// USAGE SEARCH SUGGESTIONS
const showUsageSuggestions = ref(false);
const hoverUsageIndex = ref(-1);

const usageSuggestions = computed(() => {
  if (!filterUsageSearch.value.trim()) {
    return usageHistory.value.slice(0, 8);
  }
  const q = filterUsageSearch.value.toLowerCase();
  return usageHistory.value.filter(u => 
    (u.recipe_name || "").toLowerCase().includes(q) ||
    (u.product_name || "").toLowerCase().includes(q) ||
    (u.username || "").toLowerCase().includes(q) ||
    (u.full_name || "").toLowerCase().includes(q)
  ).slice(0, 8);
});

function onUsageSearchFocus() {
  showUsageSuggestions.value = true;
}

function onUsageSearchBlur() {
  setTimeout(() => {
    showUsageSuggestions.value = false;
  }, 200);
}

function onUsageSearchKeydown(e) {
  if (!showUsageSuggestions.value || !usageSuggestions.value.length) return;
  
  if (e.key === "ArrowDown") {
    e.preventDefault();
    hoverUsageIndex.value = (hoverUsageIndex.value + 1) % usageSuggestions.value.length;
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    hoverUsageIndex.value = (hoverUsageIndex.value - 1 + usageSuggestions.value.length) % usageSuggestions.value.length;
  } else if (e.key === "Enter") {
    e.preventDefault();
    const item = usageSuggestions.value[hoverUsageIndex.value >= 0 ? hoverUsageIndex.value : 0];
    if (item) selectUsageSuggestion(item);
  } else if (e.key === "Escape") {
    showUsageSuggestions.value = false;
  }
}

function selectUsageSuggestion(item) {
  // Prioritize product name, then recipe name
  const text = item.product_name || item.recipe_name || "";
  filterUsageSearch.value = String(text).trim();
  showUsageSuggestions.value = false;
}

// IMAGE SEARCH SUGGESTIONS
const showImageSuggestions = ref(false);
const hoverImageIndex = ref(-1);

const imageSuggestions = computed(() => {
  if (!filterImageSearch.value.trim()) {
    return images.value.slice(0, 8);
  }
  const q = filterImageSearch.value.toLowerCase();
  return images.value.filter(i => 
    (i.name || "").toLowerCase().includes(q) ||
    (i.description || "").toLowerCase().includes(q) ||
    (i.recipes?.name || "").toLowerCase().includes(q)
  ).slice(0, 8);
});

function onImageSearchFocus() {
  showImageSuggestions.value = true;
}

function onImageSearchBlur() {
  setTimeout(() => {
    showImageSuggestions.value = false;
  }, 200);
}

function onImageSearchKeydown(e) {
  if (!showImageSuggestions.value || !imageSuggestions.value.length) return;
  
  if (e.key === "ArrowDown") {
    e.preventDefault();
    hoverImageIndex.value = (hoverImageIndex.value + 1) % imageSuggestions.value.length;
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    hoverImageIndex.value = (hoverImageIndex.value - 1 + imageSuggestions.value.length) % imageSuggestions.value.length;
  } else if (e.key === "Enter") {
    e.preventDefault();
    const img = imageSuggestions.value[hoverImageIndex.value >= 0 ? hoverImageIndex.value : 0];
    if (img) selectImageSuggestion(img);
  } else if (e.key === "Escape") {
    showImageSuggestions.value = false;
  }
}

function selectImageSuggestion(img) {
  filterImageSearch.value = String(img.name).trim();
  showImageSuggestions.value = false;
}

// Filtered Data Computed Properties
const filteredRecipes = computed(() => {
  let res = recipes.value;
  
  if (filterRecipeStatus.value !== 'all') {
    const isActive = filterRecipeStatus.value === 'active';
    res = res.filter(r => !!r.is_active === isActive);
  }
  
  if (filterRecipeSearch.value.trim()) {
    const q = filterRecipeSearch.value.toLowerCase();
    res = res.filter(r => 
      (r.name || "").toLowerCase().includes(q)
    );
  }
  
  // Sorting
  res.sort((a, b) => {
    const [field, order] = sortRecipeBy.value.split('-');
    const modifier = order === 'asc' ? 1 : -1;
    
    if (field === 'name') {
      return modifier * (a.name || "").localeCompare(b.name || "");
    } else if (field === 'time') {
      return modifier * ((a.preparation_time || 0) - (b.preparation_time || 0));
    } else if (field === 'portions') {
      return modifier * ((a.portions || 0) - (b.portions || 0));
    }
    return 0;
  });
  
  return res;
});

// Watcher to reset pagination
watch([filterRecipeSearch, filterRecipeStatus, sortRecipeBy], () => {
  recipePage.value = 1;
});


const filteredUsageHistory = computed(() => {
  let res = usageHistory.value;
  
  if (filterUsageDateFrom.value) {
    // Create local start of day
    const fromDate = new Date(filterUsageDateFrom.value + 'T00:00:00');
    res = res.filter(u => {
      const recordDate = new Date(u.created_at);
      return recordDate >= fromDate;
    });
  }
  
  if (filterUsageDateTo.value) {
    // Create local end of day
    const toDate = new Date(filterUsageDateTo.value + 'T23:59:59.999');
    res = res.filter(u => {
      const recordDate = new Date(u.created_at);
      return recordDate <= toDate;
    });
  }
  
  if (filterUsageSearch.value.trim()) {
    const q = filterUsageSearch.value.toLowerCase();
    res = res.filter(u => 
      (u.recipe_name || "").toLowerCase().includes(q) ||
      (u.product_name || "").toLowerCase().includes(q) ||
      (u.username || "").toLowerCase().includes(q) ||
      (u.full_name || "").toLowerCase().includes(q)
    );
  }
  
  // Sorting
  res.sort((a, b) => {
    const [field, order] = sortUsageBy.value.split('-');
    const modifier = order === 'asc' ? 1 : -1;
    
    if (field === 'date') {
      return modifier * (new Date(a.created_at) - new Date(b.created_at));
    } else if (field === 'qty') {
      return modifier * ((a.quantity_used || 0) - (b.quantity_used || 0));
    }
    return 0;
  });
  
  return res;
});

const filteredImages = computed(() => {
  let res = images.value;
  
  if (filterImageSearch.value.trim()) {
    const q = filterImageSearch.value.toLowerCase();
    res = res.filter(i => 
      (i.name || "").toLowerCase().includes(q) ||
      (i.description || "").toLowerCase().includes(q) ||
      (i.recipes?.name || "").toLowerCase().includes(q)
    );
  }
  
  // Sorting
  res.sort((a, b) => {
    const [field, order] = sortImageBy.value.split('-');
    const modifier = order === 'asc' ? 1 : -1;
    
    if (field === 'name') {
      return modifier * (a.name || "").localeCompare(b.name || "");
    }
    return 0;
  });
  
  return res;
});

// Watchers to reset pagination
watch([filterUsageSearch, filterUsageDateFrom, filterUsageDateTo, sortUsageBy], () => {
  usagePage.value = 1;
});

watch([filterImageSearch, sortImageBy], () => {
  imagePage.value = 1;
});

const paginatedImages = computed(() => {
  const start = (imagePage.value - 1) * imageItemsPerPage.value;
  const end = start + imageItemsPerPage.value;
  return filteredImages.value.slice(start, end);
});

const imageTotalPages = computed(() => {
  return Math.ceil(filteredImages.value.length / imageItemsPerPage.value);
});

function nextImagePage() {
  if (imagePage.value < imageTotalPages.value) {
    imagePage.value++;
  }
}

function prevImagePage() {
  if (imagePage.value > 1) {
    imagePage.value--;
  }
}

// FILTER HELPERS
const hasActiveFilters = computed(() => {
  if (activeTab.value === 'recipes') {
    return filterRecipeSearch.value.trim() !== "" || filterRecipeStatus.value !== "all" || sortRecipeBy.value !== "name-asc";
  } else if (activeTab.value === 'usage') {
    return filterUsageSearch.value.trim() !== "" || filterUsageDateFrom.value !== "" || filterUsageDateTo.value !== "" || sortUsageBy.value !== "date-desc";
  } else if (activeTab.value === 'images') {
    return filterImageSearch.value.trim() !== "" || sortImageBy.value !== "name-asc";
  }
  return false;
});

function clearFilters() {
  if (activeTab.value === 'recipes') {
    filterRecipeSearch.value = "";
    filterRecipeStatus.value = "all";
    sortRecipeBy.value = "name-asc";
  } else if (activeTab.value === 'usage') {
    filterUsageSearch.value = "";
    filterUsageDateFrom.value = "";
    filterUsageDateTo.value = "";
    sortUsageBy.value = "date-desc";
  } else if (activeTab.value === 'images') {
    filterImageSearch.value = "";
    sortImageBy.value = "name-asc";
  }
}

// Helper functions for modals
function showToast(message, type = 'success') {
  notification.value = { type, message };
  showNotification.value = true;
  setTimeout(() => {
    showNotification.value = false;
  }, 3000);
}

function showConfirm(title, message, onConfirm, confirmText = 'Confirmar', cancelText = 'Cancelar') {
  confirmModal.value = { title, message, onConfirm, confirmText, cancelText };
  showConfirmModal.value = true;
}

function closeConfirm() {
  showConfirmModal.value = false;
}

function handleConfirm() {
  if (confirmModal.value.onConfirm) {
    confirmModal.value.onConfirm();
  }
  closeConfirm();
}

onMounted(async () => {
  await loadData();
  stockAlertsChannel.value = subscribeToStockAlerts((p) => {
    const name = String((p && (p.name || p.ingredient_name)) || "Ingrediente");
    const init = Number((p && p.initial) || 0);
    const cur = Number((p && p.current) || 0);
    const perc = init > 0 ? Math.round((cur / init) * 100) : 0;
    showToast(`Stock bajo: ${name} (${perc}% de ${init})`, "warning");
  });
});

onUnmounted(() => {
  try {
    const ch = stockAlertsChannel.value;
    if (ch && typeof ch.unsubscribe === "function") ch.unsubscribe();
  } catch { }
});

async function loadData() {
  loading.value = true;
  try {
    [recipes.value, usageHistory.value, images.value, products.value] = await Promise.all([
      listRecipes(),
      listIngredientUsage(),
      listIngredientImages(),
      getAllIngredientStocks()
    ]);

    // Ensure all active recipes have a start time
    recipes.value.forEach(r => {
      if (r.is_active && r.preparation_time && !r.timer_started_at && !r.status_message) {
        const now = new Date().toISOString();
        r.timer_started_at = now;
        updateRecipe(r.id_recipes, { timer_started_at: now }).catch(console.error);
      }
    });

    startGlobalTimer();
  } finally {
    loading.value = false;
  }
}

function getStockForProduct(productName) {
  if (!productName) return '-';
  const prod = products.value.find(p => 
    String(p.ingredient_name).toLowerCase() === String(productName).toLowerCase()
  );
  const qty = Number(prod?.stock_qty || 0);
  return qty < 0 ? '0' : `${qty}`;
}

function getDisplayStock(ing) {
  // 1. Si el producto escrito coincide exactamente con uno existente, mostrar su stock real
  if (ing.product_name) {
    const prod = products.value.find(p => 
      String(p.ingredient_name).toLowerCase() === String(ing.product_name).toLowerCase()
    );
    if (prod) {
      return Number(prod.stock_qty || 0);
    }
  }
  
  // 2. Si no coincide (estamos editando/corrigiendo), mantener el stock original visualmente
  // para no mostrar "0" agresivamente.
  if (ing.current_stock !== undefined) {
    return ing.current_stock;
  }
  
  // 3. Si es nuevo y no coincide, entonces sí es 0
  return 0;
}

async function fetchIngredientsWithStock(recipeId) {
  const ingredients = await getRecipeIngredients(recipeId);
  try {
    const names = ingredients.map(i => i.product_name).filter(Boolean);
    if (names.length > 0) {
      const stocks = await getIngredientStockByNames(names);
      const stockMap = new Map((stocks || []).map(s => [String(s.ingredient_name).toLowerCase(), s.stock_qty]));
      ingredients.forEach(ing => {
        ing.current_stock = stockMap.get(String(ing.product_name).toLowerCase()) || 0;
      });
    }
  } catch (err) {
    console.error("Error fetching stock for recipe ingredients", err);
  }
  return ingredients;
}

// RECIPE FUNCTIONS
async function openRecipeModal(recipe = null) {
  if (recipe) {
    recipeForm.value = { ...recipe };
    selectedRecipe.value = recipe;
    // Load ingredients for existing recipe
    recipeIngredients.value = await fetchIngredientsWithStock(recipe.id_recipes);
  } else {
    recipeForm.value = { name: "", description: "", portions: 1, unit: "porciones", preparation_time: null, notes: "" };
    selectedRecipe.value = null;
    recipeIngredients.value = []; // Clear for new recipe
  }
  ingredientForm.value = { ingredient_name: "", product_id: null, quantity: 1, unit: "kg", notes: "" };
  showRecipeModal.value = true;
  setPrepFromForm();
}

async function addIngredientToRecipe() {
  const name = String(ingredientForm.value.ingredient_name || "").trim();
  const qty = Number(ingredientForm.value.quantity);
  
  if (!name || !Number.isFinite(qty) || qty <= 0) {
    showAlert({ title: "Error", text: "Nombre y cantidad válidos requeridos", icon: "error" });
    return;
  }

  // If editing existing recipe, add directly to DB
  if (selectedRecipe.value && selectedRecipe.value.id_recipes) {
    try {
      await addRecipeIngredient({
        recipe_id: selectedRecipe.value.id_recipes,
        ...ingredientForm.value
      });
      recipeIngredients.value = await fetchIngredientsWithStock(selectedRecipe.value.id_recipes);
      showToast("Ingrediente agregado");
    } catch (err) {
      showAlert({ title: "Error", text: err.message, icon: "error" });
      return;
    }
  } else {
    // If new recipe, add to local list
    recipeIngredients.value.push({
      product_name: name,
      quantity: qty,
      unit: ingredientForm.value.unit,
      notes: ingredientForm.value.notes,
      // Add temporary ID for keying
      temp_id: Date.now()
    });
  }
  
  // Reset form
  ingredientForm.value = { ingredient_name: "", product_id: null, quantity: 1, unit: "kg", notes: "" };
}

async function removeIngredientFromRecipe(ing) {
  if (ing.id_recipe_ingredients) {
    // Existing ingredient in DB
    const result = await confirmAction({
      title: "¿Eliminar ingrediente?",
      type: "warning",
      confirmText: "Sí, eliminar",
      cancelText: "Cancelar"
    });
    if (result.isConfirmed) {
      try {
        await deleteRecipeIngredient(ing.id_recipe_ingredients);
        recipeIngredients.value = await fetchIngredientsWithStock(selectedRecipe.value.id_recipes);
      } catch (err) {
        showToast(err.message, "error");
      }
    }
  } else {
    // Local ingredient (not saved yet)
    recipeIngredients.value = recipeIngredients.value.filter(i => i !== ing);
  }
}

async function saveRecipe() {
  try {
    let recipeId;
    if (selectedRecipe.value) {
      // Detectar cambio de tiempo para reiniciar timer
      const oldMinutes = selectedRecipe.value.preparation_time || 0;
      const formTime = recipeForm.value.preparation_time;
      let newMinutes = 0;

      if (typeof formTime === 'number') {
        newMinutes = formTime;
      } else if (typeof formTime === 'string' && formTime.includes(':')) {
        const [h, m] = formTime.split(':').map(Number);
        newMinutes = (h * 60) + m;
      }

      // Si el tiempo es 0 o nulo, marcar como listo inmediatamente
      if (newMinutes === 0) {
         recipeForm.value.status_message = "Su platillo esta listo en unos momentos su mesero se lo lleva";
         recipeForm.value.timer_started_at = null;
      } else if (newMinutes !== oldMinutes) {
         // Si cambió el tiempo y es > 0, reiniciar timer
         recipeForm.value.timer_started_at = new Date().toISOString();
         recipeForm.value.status_message = null; 
      }

      await updateRecipe(selectedRecipe.value.id_recipes, recipeForm.value);
      recipeId = selectedRecipe.value.id_recipes;
      
      // Actualizar ingredientes también
      if (recipeIngredients.value && recipeIngredients.value.length > 0) {
        // Usamos Promise.all para guardar todos los cambios pendientes en la tabla
        await Promise.all(recipeIngredients.value.map(async (ing) => {
          // Solo actualizamos ingredientes que ya existen en DB (tienen ID)
          // Los nuevos se agregan via addIngredientToRecipe inmediatamente
          if (ing.id_recipe_ingredients) {
             const { ok } = await updateRecipeIngredient(ing.id_recipe_ingredients, {
                ingredient_name: ing.product_name,
                quantity: ing.quantity,
                unit: ing.unit,
             });
             if (ok) {
               // Sincronizar stock silenciosamente
               await adjustIngredientStock(ing.product_name, Number(ing.quantity), "Sincronización desde Receta (Guardado Global)");
             }
          }
        }));
      }
      
      showToast("Receta actualizada correctamente");
    } else {
      // Logica para nueva receta con timer inicial
      const formTime = recipeForm.value.preparation_time;
      let newMinutes = 0;
      if (typeof formTime === 'number') newMinutes = formTime;
      else if (typeof formTime === 'string' && formTime.includes(':')) {
         const [h, m] = formTime.split(':').map(Number);
         newMinutes = (h * 60) + m;
      }

      if (newMinutes === 0) {
          recipeForm.value.status_message = "Su platillo esta listo en unos momentos su mesero se lo lleva";
          recipeForm.value.timer_started_at = null;
      } else {
          recipeForm.value.status_message = null;
          recipeForm.value.timer_started_at = new Date().toISOString();
      }

      const newRecipe = await createRecipe(recipeForm.value);
      // newRecipe might be the object or array depending on implementation
      // Assuming createRecipe returns the inserted row(s)
      recipeId = newRecipe && newRecipe[0] ? newRecipe[0].id_recipes : (newRecipe ? newRecipe.id_recipes : null);
      
      if (recipeId && recipeIngredients.value.length > 0) {
        // Save pending ingredients
        for (const ing of recipeIngredients.value) {
           const qty = Number(ing.quantity);
           if (!Number.isFinite(qty) || qty <= 0) {
             throw new Error(`Cantidad inválida para ingrediente: ${ing.product_name || 'Desconocido'}`);
           }
           await addRecipeIngredient({
            recipe_id: recipeId,
            ingredient_name: ing.product_name,
            quantity: qty,
            unit: ing.unit,
            notes: ing.notes
          });
        }
      }
      showToast("Receta creada correctamente");
    }
    showRecipeModal.value = false;
    await loadData();
  } catch (err) {
    console.error(err);
    showToast(err.message || "Error al guardar receta", "error");
  }
}

async function deleteRecipeAction(recipe) {
  const result = await confirmAction({
    title: "¿Eliminar receta?",
    html: `¿Estás seguro de eliminar la receta <b>${recipe.name}</b>? Esta acción no se puede deshacer.`,
    confirmText: "Sí, eliminar",
    cancelText: "Cancelar",
    type: "warning"
  });

  if (result.isConfirmed) {
    try {
      await deleteRecipe(recipe.id_recipes);
      showToast("Receta eliminada correctamente");
      await loadData();
    } catch (err) {
      showToast(err.message, "error");
    }
  }
}

async function generateDescription() {
  if (!recipeForm.value.name || !recipeForm.value.name.trim()) {
    showAlert({ title: "Error", text: "Ingresa un nombre primero", icon: "error" });
    return;
  }

  generatingDescription.value = true;
  
  // PRIMERO: Probar con descripción hardcoded para aislar el problema
  try {
    console.log('🧪 Generando descripción automática...');
    
    const descriptions = [
      `${recipeForm.value.name}: Exquisito plato tradicional preparado con ingredientes frescos de alta calidad seleccionados cuidadosamente. Nuestra receta signature sigue técnicas culinarias auténticas transmitidas por generaciones, cocinado lentamente a fuego lento para resaltar los sabores naturales y lograr una textura perfecta que se deshace en la boca. Cada bocado ofrece una experiencia armoniosa de sabores equilibrados, ideal para compartir en reuniones familiares o ocasiones especiales donde la buena comida es protagonista.`,
      
      `${recipeForm.value.name}: Receta casera maestra que combina la tradición con la innovación culinaria contemporánea. Elaborada con ingredientes premium de temporada, esta creación destaca por su presentación espectacular y su sabor inigualable que sorprende hasta los paladares más exigentes. El proceso de preparación artesanal garantiza la preservación de nutrientes esenciales mientras maximiza el sabor natural de cada componente. Perfecta para quienes buscan una experiencia gastronómica memorable y auténtica.`,
      
      `${recipeForm.value.name}: Creación culinaria excepcional que fusiona lo mejor de la cocina tradicional con técnicas modernas de preparación. Nuestros chefs expertos utilizan métodos precisos de cocción y combinaciones únicas de especias para transformar ingredientes simples en una obra maestra gastronómica. Cada elemento es cuidadosamente seleccionado y preparado para crear un equilibrio perfecto entre texturas, sabores y aromas que evocan emociones y crean recuerdos inolvidables alrededor de la mesa.`,
      
      `${recipeForm.value.name}: Plato insignia que representa la esencia de la cocina auténtica con un toque de elegancia contemporánea. Preparado siguiendo rigurosos estándares de calidad y utilizando técnicas ancestrales perfeccionadas con el tiempo, este plato ofrece una experiencia sensorial completa que deleita la vista, el olfato y el paladar. Los ingredientes frescos son adquiridos directamente de productores locales, garantizando máxima frescura y sostenibilidad en cada preparación.`,
      
      `${recipeForm.value.name}: Obra culinaria excepcional que demuestra la maestría en la combinación de sabores y texturas. Esta receta exclusiva ha sido perfeccionada durante años para lograr el equilibrio perfecto entre tradición e innovación. Utilizamos solo ingredientes de primera categoría, muchos de ellos orgánicos y de origen local, preparados en el momento exacto para preservar sus propiedades nutricionales y maximizar su sabor natural. Ideal para celebraciones especiales o para aquellos momentos en que se desea disfrutar de una experiencia culinaria verdaderamente extraordinaria.`
    ];
    
    const randomIndex = Math.floor(Math.random() * descriptions.length);
    recipeForm.value.description = descriptions[randomIndex];
    
    console.log('✅ Descripción generada:', recipeForm.value.description);
    showAlert({ title: "Éxito", text: "Descripción generada automáticamente", icon: "success" });
  } catch (testError) {
    console.error('❌ Error generando descripción:', testError);
    showAlert({ title: "Error", text: "No se pudo generar la descripción", icon: "error" });
  } finally {
    // IMPORTANTE: Siempre resetear el estado de carga
    generatingDescription.value = false;
  }
  
  // Salir de la función para no ejecutar la API
  return;

  // Si el hardcoded funciona, intentar con la API
  try {
    // Debug: mostrar todas las variables de entorno
    console.log('🔍 Todas las variables VITE_:');
    Object.keys(import.meta.env).forEach(key => {
      if (key.startsWith('VITE_')) {
        console.log(`  ${key}:`, import.meta.env[key] ? '✅' : '❌');
      }
    });

    const apiKey = import.meta.env.VITE_MISTRAL_API_KEY;
    const apiUrl = import.meta.env.VITE_MISTRAL_API_URL;
    const model = import.meta.env.VITE_MISTRAL_MODEL;

    console.log('🔍 Configuración Mistral:', {
      hasApiKey: !!apiKey,
      hasApiUrl: !!apiUrl,
      hasModel: !!model,
      apiUrl: apiUrl
    });

    if (!apiKey || !apiUrl || !model) {
      throw new Error('Faltan variables de entorno de Mistral AI');
    }

    // Agregar headers adicionales para evitar problemas de CORS
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
      'Accept': 'application/json'
    };

    console.log('🚀 Enviando petición a:', apiUrl);
    console.log('📋 Headers:', { ...headers, Authorization: 'Bearer [HIDDEN]' });

    // Intentar con diferentes configuraciones
    let response;
    try {
      // Primer intento: con CORS explícito
      response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        mode: 'cors',
        body: JSON.stringify({
          model: model,
          messages: [
            {
              role: 'user',
              content: `Genera una descripción breve y atractiva (máximo 2-3 líneas) para una receta llamada "${recipeForm.value.name}". La descripción debe ser en español, profesional y mencionar ingredientes típicos o características principales del platillo. NO usa formato markdown, NO usa asteriscos ni negritas, solo texto plano.`
            }
          ],
          temperature: 0.7,
          max_tokens: 150
        })
      });
    } catch (corsError) {
      console.warn('⚠️ Error con CORS, intentando sin mode:', corsError);
      // Segundo intento: sin mode cors
      response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          model: model,
          messages: [
            {
              role: 'user',
              content: `Genera una descripción breve y atractiva (máximo 2-3 líneas) para una receta llamada "${recipeForm.value.name}". La descripción debe ser en español, profesional y mencionar ingredientes típicos o características principales del platillo. NO usa formato markdown, NO usa asteriscos ni negritas, solo texto plano.`
            }
          ],
          temperature: 0.7,
          max_tokens: 150
        })
      });
    }

    console.log('📊 Respuesta HTTP:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Error de API:', errorText);
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    let description = data.choices[0]?.message?.content?.trim();

    if (description) {
      description = description.replace(/\*\*/g, '').replace(/\*/g, '').replace(/##/g, '').replace(/#/g, '');
      recipeForm.value.description = description;
    }
  } catch (err) {
    console.error('❌ Error completo:', err);
    
    let message = "No se pudo generar la descripción.";
    if (err.message.includes('Failed to fetch')) {
      message = "Error de conexión a la API. Verifica tu conexión o la configuración.";
    } else if (err.message.includes('CORS')) {
      message = "Error de CORS. La API no permite peticiones desde este dominio.";
    } else if (err.message.includes('401')) {
      message = "API key inválida.";
    }
    
    showAlert({ title: "Error", text: message, icon: "error" });
  } finally {
    generatingDescription.value = false;
  }
}

async function viewRecipeIngredients(recipe) {
  selectedRecipe.value = recipe;
  recipeIngredients.value = await fetchIngredientsWithStock(recipe.id_recipes);
  showIngredientsModal.value = true;
}

async function addIngredient() {
  const name = String(ingredientForm.value.ingredient_name || "").trim();
  const qty = Number(ingredientForm.value.quantity);

  if (!name || !Number.isFinite(qty) || qty <= 0) {
    showAlert({ title: "Error", text: "Nombre y cantidad válidos requeridos (mayor a 0)", icon: "error" });
    return;
  }

  try {
    await addRecipeIngredient({
      recipe_id: selectedRecipe.value.id_recipes,
      ...ingredientForm.value
    });
    recipeIngredients.value = await fetchIngredientsWithStock(selectedRecipe.value.id_recipes);
    ingredientForm.value = { ingredient_name: "", product_id: null, quantity: 1, unit: "kg", notes: "" };
  } catch (err) {
    showAlert({ title: "Error", text: err.message, icon: "error" });
  }
}

async function removeIngredient(id) {
  const result = await confirmAction({
    title: "¿Eliminar ingrediente?",
    type: "warning",
    confirmText: "Sí, eliminar",
    cancelText: "Cancelar"
  });
  if (result.isConfirmed) {
    await deleteRecipeIngredient(id);
    recipeIngredients.value = await fetchIngredientsWithStock(selectedRecipe.value.id_recipes);
  }
}

async function saveIngredientRow(ing) {
  const qty = Number(ing.quantity);
  if (!Number.isFinite(qty) || qty <= 0) {
    showAlert({ title: "Error", text: "La cantidad debe ser mayor a 0", icon: "error" });
    return;
  }

  try {
    // Obtener estado original antes de guardar
    const originalIng = await getRecipeIngredientById(ing.id_recipe_ingredients);

    const { ok } = await updateRecipeIngredient(ing.id_recipe_ingredients, {
      ingredient_name: ing.product_name,
      quantity: ing.quantity,
      unit: ing.unit,
    });
    if (!ok) throw new Error("No se pudo guardar la receta");

    // Sincronizar Stock SOLO si cambió la cantidad o unidad
    // Si solo cambia el nombre (corrección de typo), no reiniciamos stock.
    const oldQty = originalIng ? Number(originalIng.quantity) : 0;
    const newQty = Number(ing.quantity);
    const oldUnit = originalIng ? originalIng.unit : "";
    const newUnit = ing.unit;

    if (oldQty !== newQty || oldUnit !== newUnit) {
      const targetStock = Number(ing.quantity);
      const { ok: stockOk, error: stockError } = await adjustIngredientStock(ing.product_name, targetStock, "Sincronización desde Receta");

      if (!stockOk) {
         console.error("Stock adjustment failed:", stockError);
         showToast("Receta guardada, pero error al actualizar stock", "warning");
      } else {
         showToast("Receta y Stock actualizados");
      }
    } else {
      showToast("Receta actualizada (Stock sin cambios)");
    }
    
    // Recargar datos
    const [freshIngredients, freshStocks] = await Promise.all([
        fetchIngredientsWithStock(selectedRecipe.value.id_recipes),
        getAllIngredientStocks()
    ]);
    recipeIngredients.value = freshIngredients;
    products.value = freshStocks;

  } catch (err) {
    showAlert({ title: "Error", text: err.message, icon: "error" });
  }
}

// USAGE FUNCTIONS
async function openUsageModal() {
  usageForm.value = { recipe_id: null, portions_made: 1, notes: "", items: [] };
  showUsageModal.value = true;
}

async function onRecipeSelect() {
  if (!usageForm.value.recipe_id) return;
  let ingredients = await getRecipeIngredients(usageForm.value.recipe_id);
  if (!ingredients || !ingredients.length) {
    ingredients = await getRecipeIngredientsSafe(usageForm.value.recipe_id);
  }

  // Fetch current stock for these ingredients
  let stockMap = new Map();
  try {
    const names = ingredients.map(i => i.product_name).filter(Boolean);
    if (names.length > 0) {
      const stocks = await getIngredientStockByNames(names);
      if (stocks && stocks.length) {
        stocks.forEach(s => stockMap.set(String(s.ingredient_name).toLowerCase(), s.stock_qty));
      }
    }
  } catch (err) {
    console.error("Error fetching stock", err);
  }

  usageForm.value.items = ingredients.map(ing => {
    const stock = stockMap.get(String(ing.product_name).toLowerCase()) || 0;
    // Usar la cantidad definida en la receta, independientemente del stock actual.
    // El usuario verá 0 en stock si no hay, pero la cantidad requerida aparecerá.
    const defaultQty = ing.quantity * usageForm.value.portions_made;
    
    return {
      product_id: ing.product_id,
      product_name: ing.product_name,
      quantity: defaultQty,
      unit: ing.unit,
      current_stock: stock
    };
  });
}

async function saveUsage() {
  const hasItems = usageForm.value.items && usageForm.value.items.length > 0;

  // VALIDACIÓN: Si el stock está por debajo del 50%, no permitir dejarlo en 0.
  if (hasItems) {
    try {
      const names = usageForm.value.items.map(i => i.product_name);
      const statuses = await getStockStatus(names);
      const statusMap = new Map((statuses || []).map(s => [String(s.ingredient_name).toLowerCase(), s]));

      for (const item of usageForm.value.items) {
        const name = String(item.product_name || "").trim();
        const lowerName = name.toLowerCase();
        const status = statusMap.get(lowerName);
        
        if (!status) continue;

        const cur = status.current_stock;
        const use = Number(item.quantity || 0);

        // Validar basado en el "Estado de Refill" (Last Refill Logic)
        // Si el ratio es <= 0.5 (Stock bajo relativo a la última recarga)
        if (status.ratio <= 0.5) {
            // Y el uso pretendido dejaría el stock en 0 o menos
            if (use >= cur) {
              showAlert({
                title: "Stock Crítico",
                text: `El stock de "${name}" está al ${status.percent}%. No se permite agotarlo completamente cuando está bajo.`,
                icon: "warning"
              });
              return; 
            }
        }
      }
    } catch (err) {
      console.error("Error validando stock:", err);
      // En caso de error de validación técnica, permitimos continuar o mostramos error?
      // Mejor mostrar error para evitar inconsistencias.
      showAlert({ title: "Error", text: "Error verificando niveles de stock", icon: "error" });
      return;
    }
  }

  const message = hasItems
    ? `<p>Esto descontará automáticamente del inventario:</p><ul style="text-align:left">${usageForm.value.items.map(i => `<li>${i.product_name}: ${i.quantity} ${i.unit}</li>`).join('')}</ul>`
    : `<p>No hay ingredientes asociados a esta selección.</p><p>Se registrará el uso de <b>${usageForm.value.portions_made}</b> porciones <i>sin</i> descontar inventario.</p>`;

  const result = await confirmAction({
    title: "Confirmar Registro",
    html: message,
    type: hasItems ? "warning" : "info",
    confirmText: "Sí, registrar",
    cancelText: "Cancelar"
  });

  if (result.isConfirmed) {
    try {
      const user = currentUser();
      await createIngredientUsage({
        ...usageForm.value,
        user_id: user?.id || null
      });
      const okText = hasItems ? "Inventario actualizado automáticamente" : "Registro guardado sin ajuste de inventario";
      let html = `<p>${okText}</p>`;
      if (hasItems) {
        html += `<p>Cantidad descontada por ingrediente:</p><ul style="text-align:left">${usageForm.value.items.map(i => `<li>${i.product_name}: ${i.quantity} ${i.unit}</li>`).join('')}</ul>`;

        const names = usageForm.value.items.map(i => i.product_name);
        const stocks = await getIngredientStockByNames(names);
        const initials = await getInitialStockByNames(names);
        const imap = new Map((initials || []).map(i => [String(i.ingredient_name || ''), Number(i.initial_qty || 0)]));
        const lows = (stocks || []).filter(s => {
          const init = Number(imap.get(String(s.ingredient_name || '')) || 0);
          if (!(init > 0)) return false;
          return Number(s.stock_qty || 0) <= init * 0.5;
        });
        if (lows.length) {
          showToast(`Stock bajo: ${lows.map(l => l.ingredient_name).join(', ')}`, 'warning');
          try {
            lows.forEach(l => {
              const init = Number(imap.get(String(l.ingredient_name || '')) || 0);
              const cur = Number(l.stock_qty || 0);
              const perc = init > 0 ? Math.round((cur / init) * 100) : 0;
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('ops:stock:low', { detail: { name: l.ingredient_name, current: cur, initial: init, percent: perc } }));
              }
            });
          } catch { }
        }
      }
      showAlert({ title: "¡Registrado!", html, icon: "success" });
      showUsageModal.value = false;
      await loadData();
    } catch (err) {
      showAlert({ title: "Error", text: err.message, icon: "error" });
    }
  }
}

function addUsageItem() {
  const name = String(usageItemForm.value.product_name || "").trim();
  const qty = Number(usageItemForm.value.quantity);
  if (!name || !Number.isFinite(qty) || qty <= 0) return;
  usageForm.value.items.push({
    product_id: null,
    product_name: name,
    quantity: qty,
    unit: usageItemForm.value.unit || null,
  });
  usageItemForm.value = { product_name: "", product_id: null, quantity: 1, unit: "kg" };
}

async function removeUsageItem(index) {
  const result = await confirmAction({
    title: "¿Eliminar ingrediente?",
    text: "Esta acción lo quitará de la lista de uso.",
    type: "warning",
    confirmText: "Sí, eliminar",
    cancelText: "Cancelar"
  });

  if (result.isConfirmed) {
    usageForm.value.items.splice(index, 1);
  }
}

// IMAGE FUNCTIONS
async function openImageModal(img = null) {
  if (img) {
    isEditingImage.value = true;
    editingImageId.value = img.id_ingredient_images;
    imageForm.value = {
      name: img.name,
      description: img.description || "",
      recipe_id: img.recipe_id || null,
      file: null,
      preview: img.image_url || (img.image_base64 ? `data:image/jpeg;base64,${img.image_base64}` : null)
    };
  } else {
    isEditingImage.value = false;
    editingImageId.value = null;
    imageForm.value = { name: "", description: "", recipe_id: null, file: null, preview: null };
  }
  showImageModal.value = true;
}

function onImageSelect(event) {
  const file = event.target.files[0];
  if (!file) return;
  imageForm.value.file = file;
  const reader = new FileReader();
  reader.onload = (e) => {
    imageForm.value.preview = e.target.result;
  };
  reader.readAsDataURL(file);
}

async function saveImage() {
  if (!imageForm.value.name) {
    showAlert({ title: "Error", text: "Nombre requerido", icon: "error" });
    return;
  }

  // Validación: Nueva imagen requiere archivo. Edición no (opcional).
  if (!isEditingImage.value && !imageForm.value.file) {
    showAlert({ title: "Error", text: "Imagen requerida", icon: "error" });
    return;
  }

  try {
    if (isEditingImage.value) {
      await updateIngredientImage(editingImageId.value, {
        name: imageForm.value.name,
        description: imageForm.value.description,
        recipe_id: imageForm.value.recipe_id,
        file: imageForm.value.file
      });
      showAlert({ title: "¡Actualizada!", icon: "success" });
    } else {
      let base64 = null;
      if (imageForm.value.preview && imageForm.value.preview.includes(',')) {
        base64 = imageForm.value.preview.split(',')[1];
      }
      const user = currentUser();
      await createIngredientImage({
        name: imageForm.value.name,
        description: imageForm.value.description,
        image_base64: base64,
        file: imageForm.value.file,
        mime: imageForm.value.file.type,
        user_id: user?.id || null,
        recipe_id: imageForm.value.recipe_id || null
      });
      showAlert({ title: "¡Guardada!", icon: "success" });
    }
    showImageModal.value = false;
    await loadData();
  } catch (err) {
    showNotification.value = true;
    notification.value = { type: 'error', message: err.message || 'Error al guardar la imagen' };
  }
}

async function deleteImage(id) {
  const result = await confirmAction({
    title: "¿Eliminar imagen?",
    type: "warning",
    confirmText: "Sí, eliminar",
    cancelText: "Cancelar"
  });
  if (result.isConfirmed) {
    await deleteIngredientImage(id);
    await loadData();
  }
}

// STATS
const stats = computed(() => ({
  totalRecipes: recipes.value.length,
  activeRecipes: recipes.value.filter(r => r.is_active).length,
  totalUsages: usageHistory.value.length,
  totalImages: images.value.length
}));

const canSubmitUsage = computed(() => {
  const f = usageForm.value;
  return Number(f.portions_made || 0) > 0 && (!!f.recipe_id || (f.items && f.items.length > 0));
});

const hourOptions = computed(() => Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")));
const minuteOptions = computed(() => Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0")));

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr.replace(" ", "T"));
    return d.toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return dateStr;
  }
}

function handleImageError(event, fallbackBase64 = null, mime = null) {
  if (fallbackBase64) {
    event.target.src = `data:${mime || 'image/jpeg'};base64,${fallbackBase64}`;
  } else {
    // Placeholder simple (gris con texto Error)
    event.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23eee'/%3E%3Ctext x='50' y='50' font-family='Arial' font-size='12' fill='%23999' text-anchor='middle' dy='.3em'%3EError%3C/text%3E%3C/svg%3E";
  }
}
</script>

<template>
  <main class="wrap">
    <header class="top">
      <h1 class="title">Gestión de Ingredientes</h1>
      <p class="subtitle">Recetas, uso de ingredientes e imágenes</p>
    </header>

    <!-- Tabs -->
    <div class="tabs">
      <button :class="['tab', 'tab-recipes', { active: activeTab === 'recipes' }]" @click="activeTab = 'recipes'">
        <span class="material-symbols-rounded">restaurant_menu</span>
        Recetas
      </button>
      <button :class="['tab', 'tab-usage', { active: activeTab === 'usage' }]" @click="activeTab = 'usage'">
        <span class="material-symbols-rounded">inventory</span>
        Uso de Ingredientes
      </button>
      <button :class="['tab', 'tab-images', { active: activeTab === 'images' }]" @click="activeTab = 'images'">
        <span class="material-symbols-rounded">image</span>
        Imágenes
      </button>
    </div>

    <!-- Stats Cards -->
    <section class="stats-grid">
      <div class="stat-card stat-recipes">
        <span class="material-symbols-rounded stat-icon">restaurant_menu</span>
        <div class="stat-content">
          <span class="stat-label">Total Recetas</span>
          <span class="stat-value">{{ stats.totalRecipes }}</span>
        </div>
      </div>
      <div class="stat-card stat-active">
        <span class="material-symbols-rounded stat-icon">check_circle</span>
        <div class="stat-content">
          <span class="stat-label">Recetas Activas</span>
          <span class="stat-value">{{ stats.activeRecipes }}</span>
        </div>
      </div>
      <div class="stat-card stat-usage">
        <span class="material-symbols-rounded stat-icon">inventory</span>
        <div class="stat-content">
          <span class="stat-label">Usos Registrados</span>
          <span class="stat-value">{{ stats.totalUsages }}</span>
        </div>
      </div>
      <div class="stat-card stat-images">
        <span class="material-symbols-rounded stat-icon">image</span>
        <div class="stat-content">
          <span class="stat-label">Imágenes</span>
          <span class="stat-value">{{ stats.totalImages }}</span>
        </div>
      </div>
    </section>

    <!-- ADVANCED FILTERS -->
    <section class="filters-bar">
      <!-- Recipes Filters -->
      <div v-if="activeTab === 'recipes'" class="filter-group">
        <div class="nav-search search-item">
          <span class="material-symbols-rounded search-icon">search</span>
          <input 
            v-model="filterRecipeSearch" 
            type="text" 
            placeholder="Buscar receta..." 
            class="nav-input"
            @focus="onRecipeSearchFocus"
            @blur="onRecipeSearchBlur"
            @keydown="onRecipeSearchKeydown"
          />
          <button v-if="filterRecipeSearch" class="clear-btn" @click="filterRecipeSearch = ''">
            <span class="material-symbols-rounded">close</span>
          </button>
          
          <div v-if="showRecipeSuggestions && recipeSuggestions.length > 0" class="nav-suggestions">
            <div 
              v-for="(recipe, index) in recipeSuggestions" 
              :key="recipe.id_recipes"
              class="suggestion-item"
              :class="{ active: index === hoverRecipeIndex }"
              @mouseenter="hoverRecipeIndex = index"
              @mousedown.prevent="selectRecipeSuggestion(recipe)"
            >
              <img 
                v-if="recipe.image_url" 
                :src="recipe.image_url" 
                class="suggestion-thumb" 
                @error="handleImageError($event)" 
              />
              <div v-else class="suggestion-thumb placeholder-thumb">
                <span class="material-symbols-rounded">restaurant_menu</span>
              </div>
              
              <div class="suggestion-info">
                <div class="suggestion-name">{{ recipe.name }}</div>
                <div class="suggestion-meta">{{ recipe.portions }} porciones</div>
              </div>
            </div>
          </div>
        </div>
        <div class="filter-item">
          <span class="material-symbols-rounded filter-icon">toggle_on</span>
          <select v-model="filterRecipeStatus" class="filter-select">
            <option value="all">Todos los estados</option>
            <option value="active">Activas</option>
            <option value="inactive">Inactivas</option>
          </select>
        </div>
        <div class="filter-item">
          <span class="material-symbols-rounded filter-icon">sort</span>
          <select v-model="sortRecipeBy" class="filter-select">
            <option value="name-asc">Nombre (A-Z)</option>
            <option value="name-desc">Nombre (Z-A)</option>
            <option value="time-asc">Tiempo (Menor a Mayor)</option>
            <option value="time-desc">Tiempo (Mayor a Menor)</option>
            <option value="portions-asc">Porciones (Menor a Mayor)</option>
            <option value="portions-desc">Porciones (Mayor a Menor)</option>
          </select>
        </div>
      </div>

      <!-- Usage Filters -->
      <div v-if="activeTab === 'usage'" class="filter-group">
        <div class="nav-search search-item">
          <span class="material-symbols-rounded search-icon">search</span>
          <input 
            v-model="filterUsageSearch" 
            type="text" 
            placeholder="Buscar usuario, receta, producto..." 
            class="nav-input"
            @focus="onUsageSearchFocus"
            @blur="onUsageSearchBlur"
            @keydown="onUsageSearchKeydown"
          />
          <button v-if="filterUsageSearch" class="clear-btn" @click="filterUsageSearch = ''">
            <span class="material-symbols-rounded">close</span>
          </button>
          
          <div v-if="showUsageSuggestions && usageSuggestions.length > 0" class="nav-suggestions">
            <div 
              v-for="(item, index) in usageSuggestions" 
              :key="item.unique_id || item.id_ingredient_usage"
              class="suggestion-item"
              :class="{ active: index === hoverUsageIndex }"
              @mouseenter="hoverUsageIndex = index"
              @mousedown.prevent="selectUsageSuggestion(item)"
            >
              <!-- Show Recipe Image if available -->
              <img 
                v-if="item.recipe_image" 
                :src="item.recipe_image" 
                class="suggestion-thumb" 
                @error="handleImageError($event)" 
              />
              <div v-else class="suggestion-thumb placeholder-thumb">
                <span class="material-symbols-rounded">inventory</span>
              </div>
              
              <div class="suggestion-info">
                <div class="suggestion-name">{{ item.product_name || item.recipe_name || "Uso" }}</div>
                <div class="suggestion-meta">
                  {{ item.full_name || item.username }} • {{ formatDate(item.created_at) }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="filter-item">
          <span class="material-symbols-rounded filter-icon">calendar_today</span>
          <input v-model="filterUsageDateFrom" type="date" class="filter-input date-input" title="Desde" />
        </div>
        <div class="filter-item">
          <span class="material-symbols-rounded filter-icon">event</span>
          <input v-model="filterUsageDateTo" type="date" class="filter-input date-input" title="Hasta" />
        </div>
        <div class="filter-item">
          <span class="material-symbols-rounded filter-icon">sort</span>
          <select v-model="sortUsageBy" class="filter-select">
            <option value="date-desc">Fecha (Más reciente)</option>
            <option value="date-asc">Fecha (Más antigua)</option>
            <option value="qty-desc">Cantidad (Mayor a Menor)</option>
            <option value="qty-asc">Cantidad (Menor a Mayor)</option>
          </select>
        </div>
      </div>

      <!-- Images Filters -->
      <div v-if="activeTab === 'images'" class="filter-group">
        <div class="nav-search search-item">
          <span class="material-symbols-rounded search-icon">search</span>
          <input 
            v-model="filterImageSearch" 
            type="text" 
            placeholder="Buscar imagen por nombre o descripción..." 
            class="nav-input"
            @focus="onImageSearchFocus"
            @blur="onImageSearchBlur"
            @keydown="onImageSearchKeydown"
          />
          <button v-if="filterImageSearch" class="clear-btn" @click="filterImageSearch = ''">
            <span class="material-symbols-rounded">close</span>
          </button>
          
          <div v-if="showImageSuggestions && imageSuggestions.length > 0" class="nav-suggestions">
            <div 
              v-for="(img, index) in imageSuggestions" 
              :key="img.id_ingredient_images"
              class="suggestion-item"
              :class="{ active: index === hoverImageIndex }"
              @mouseenter="hoverImageIndex = index"
              @mousedown.prevent="selectImageSuggestion(img)"
            >
              <img 
                v-if="img.image_url" 
                :src="img.image_url" 
                class="suggestion-thumb" 
                @error="handleImageError($event)" 
              />
              <img 
                v-else-if="img.image_base64"
                :src="`data:${img.mime};base64,${img.image_base64}`"
                class="suggestion-thumb"
                @error="handleImageError($event)"
              />
              <div v-else class="suggestion-thumb placeholder-thumb">
                <span class="material-symbols-rounded">image</span>
              </div>
              
              <div class="suggestion-info">
                <div class="suggestion-name">{{ img.name }}</div>
                <div class="suggestion-meta">{{ img.recipes?.name || img.description || "Sin descripción" }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="filter-item">
          <span class="material-symbols-rounded filter-icon">sort</span>
          <select v-model="sortImageBy" class="filter-select">
            <option value="name-asc">Nombre (A-Z)</option>
            <option value="name-desc">Nombre (Z-A)</option>
          </select>
        </div>
      </div>

      <!-- Clear Filters Button -->
      <button :disabled="!hasActiveFilters" @click="clearFilters" class="btn-clear-filters" title="Limpiar filtros">
        <span class="material-symbols-rounded">filter_alt_off</span>
        Limpiar
      </button>
    </section>

    <!-- RECIPES TAB -->
    <section v-if="activeTab === 'recipes'" class="tab-content">
      <div class="actions-bar">
        <button class="btn-primary" @click="openRecipeModal()">
          <span class="material-symbols-rounded">add</span>
          Nueva Receta
        </button>
      </div>

      <div v-if="loading" class="loading">Cargando...</div>
      <div v-else-if="recipes.length === 0" class="empty">No hay recetas</div>
      <div v-else class="table-scroll">
        <table class="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Imagen</th>
              <th>Nombre</th>
              <th>Porciones</th>
              <th>Tiempo (Hrs)</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(recipe, index) in paginatedRecipes" :key="recipe.id_recipes">
              <td>{{ (recipePage - 1) * recipeItemsPerPage + index + 1 }}</td>
              <td>
                <div class="table-img-container" v-if="recipe.image_url">
                  <img :src="recipe.image_url" alt="Receta" class="table-img" @error="handleImageError($event)" />
                </div>
                <span v-else class="no-img">-</span>
              </td>
              <td>{{ recipe.name }}</td>
              <td>{{ recipe.portions }}</td>
              <td>
                <div class="timer-cell">
                  <template v-if="recipe.status_message">
                    <div class="status-message-badge">
                      <span class="material-symbols-rounded status-icon">check_circle</span>
                      <span class="status-text">{{ recipe.status_message }}</span>
                    </div>
                  </template>
                  <template v-else-if="timers[recipe.id_recipes] > 0">
                    <span class="timer-display active">{{ formatTimer(timers[recipe.id_recipes]) }}</span>
                  </template>
                  <template v-else>
                    <span class="timer-display">{{ formatDuration(recipe.preparation_time) }}</span>
                  </template>
                </div>
              </td>
              <td>
                <span :class="['badge', recipe.is_active ? 'badge-success' : 'badge-inactive']">
                  {{ recipe.is_active ? "Activa" : "Inactiva" }}
                </span>
              </td>
              <td>
                <div class="action-buttons">
                  <button class="btn-small btn-view" @click="viewRecipeIngredients(recipe)" title="Ver Ingredientes">
                    <span class="material-symbols-rounded">visibility</span>
                  </button>
                  <button class="btn-small btn-edit" @click="openRecipeModal(recipe)" title="Editar">
                    <span class="material-symbols-rounded">edit</span>
                  </button>
                  <button class="btn-small btn-delete" @click="deleteRecipeAction(recipe)" title="Eliminar">
                    <span class="material-symbols-rounded">delete</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination-controls" v-if="recipeTotalPages > 1">
        <button class="btn-small btn-pagination" @click="prevRecipePage" :disabled="recipePage === 1">
          <span class="material-symbols-rounded">chevron_left</span>
        </button>
        <span>Página {{ recipePage }} de {{ recipeTotalPages }}</span>
        <button class="btn-small btn-pagination" @click="nextRecipePage" :disabled="recipePage === recipeTotalPages">
          <span class="material-symbols-rounded">chevron_right</span>
        </button>
      </div>
    </section>

    <!-- USAGE TAB -->
    <section v-if="activeTab === 'usage'" class="tab-content">
      <div class="actions-bar">
        <button class="btn-primary" @click="openUsageModal()">
          <span class="material-symbols-rounded">add</span>
          Registrar Uso
        </button>
      </div>

      <div v-if="loading" class="loading">Cargando...</div>
      <div v-else-if="usageHistory.length === 0" class="empty">No hay registros de uso</div>
      <div v-else class="table-scroll">
        <table class="data-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Imagen</th>
              <th>Receta</th>
              <th>Usuario</th>
              <th>Producto</th>
              <th>Stock Actual</th>
              <th>Cantidad</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="usage in paginatedUsageHistory" :key="usage.unique_id || usage.id_ingredient_usage">
              <td>{{ formatDate(usage.created_at) }}</td>
              <td>
                <div class="table-img-container" v-if="usage.recipe_image">
                  <img :src="usage.recipe_image" alt="Receta" class="table-img" />
                </div>
                <span v-else class="no-img">-</span>
              </td>
              <td>{{ usage.recipe_name || "-" }}</td>
              <td>{{ usage.full_name || usage.username || "-" }}</td>
              <td>{{ usage.product_name || "-" }}</td>
              <td>{{ getStockForProduct(usage.product_name) }}</td>
              <td>{{ usage.quantity }} {{ usage.unit }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination-controls" v-if="usageTotalPages > 1">
        <button class="btn-small btn-pagination" @click="prevUsagePage" :disabled="usagePage === 1">
          <span class="material-symbols-rounded">chevron_left</span>
        </button>
        <span>Página {{ usagePage }} de {{ usageTotalPages }}</span>
        <button class="btn-small btn-pagination" @click="nextUsagePage" :disabled="usagePage === usageTotalPages">
          <span class="material-symbols-rounded">chevron_right</span>
        </button>
      </div>
    </section>

    <!-- IMAGES TAB -->
    <section v-if="activeTab === 'images'" class="tab-content">
      <div class="actions-bar">
        <button class="btn-primary" @click="openImageModal()">
          <span class="material-symbols-rounded">add_photo_alternate</span>
          Capturar Imagen
        </button>
      </div>

      <div v-if="loading" class="loading">Cargando...</div>
      <div v-else-if="images.length === 0" class="empty">No hay imágenes</div>
      <div v-else class="images-container">
        <div class="images-grid">
          <div v-for="img in paginatedImages" :key="img.id_ingredient_images" class="image-card">
            <div class="image-card-header">
              <img :src="img.image_url ? img.image_url : `data:${img.mime};base64,${img.image_base64}`" :alt="img.name"
                class="image-preview" @error="handleImageError($event, img.image_base64, img.mime)" />
              <div v-if="img.recipes" class="image-recipe-badge">
                <span class="material-symbols-rounded">restaurant_menu</span>
                {{ img.recipes.name }}
              </div>
            </div>
            <div class="image-info">
              <h3 class="image-name" :title="img.name">{{ img.name }}</h3>
              <p class="image-desc">{{ img.description || "Sin descripción" }}</p>
              <div class="image-actions">
                <button class="btn-icon btn-edit" @click="openImageModal(img)" title="Editar">
                  <span class="material-symbols-rounded">edit</span>
                </button>
                <button class="btn-icon btn-delete" @click="deleteImage(img.id_ingredient_images)" title="Eliminar">
                  <span class="material-symbols-rounded">delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="pagination-controls" v-if="imageTotalPages > 1">
          <button class="btn-small btn-pagination" @click="prevImagePage" :disabled="imagePage === 1">
            <span class="material-symbols-rounded">chevron_left</span>
          </button>
          <span>Página {{ imagePage }} de {{ imageTotalPages }}</span>
          <button class="btn-small btn-pagination" @click="nextImagePage" :disabled="imagePage === imageTotalPages">
            <span class="material-symbols-rounded">chevron_right</span>
          </button>
        </div>
      </div>
    </section>

    <!-- RECIPE MODAL -->
  <div v-if="showRecipeModal" class="modal-overlay" @click.self="showRecipeModal = false">
      <div class="modal modal-large">
        <header class="modal-header">
          <h2>{{ selectedRecipe ? "Editar" : "Nueva" }} Receta</h2>
          <button class="btn-close" @click="showRecipeModal = false">
            <span class="material-symbols-rounded">close</span>
          </button>
        </header>
        <div class="modal-body">
          <div class="form-field">
            <label>Nombre</label>
            <input v-model="recipeForm.name" type="text" class="input" />
          </div>
          <div class="form-field">
            <div class="label-with-button">
              <label>Descripción</label>
              <button type="button" class="btn-ai-suggest" @click="generateDescription"
                :disabled="generatingDescription || !recipeForm.name" title="Generar descripción con IA">
                <span class="material-symbols-rounded">{{ generatingDescription ? 'progress_activity' : 'auto_awesome'
                }}</span>
                {{ generatingDescription ? 'Generando...' : 'Sugerir con IA' }}
              </button>
            </div>
            <textarea v-model="recipeForm.description" class="input" rows="3"
              placeholder="Descripción de la receta..."></textarea>
          </div>
          <div class="form-row">
            <div class="form-field">
              <label>Porciones</label>
              <input v-model.number="recipeForm.portions" type="number" min="1" class="input" />
            </div>
            <div class="form-field">
              <label>Unidad</label>
              <select v-model="recipeForm.unit" class="input">
                <option value="porciones">Porciones</option>
                <option value="gramos">Gramos</option>
                <option value="kg">Kilogramos</option>
                <option value="litros">Litros</option>
                <option value="ml">Mililitros</option>
                <option value="unidades">Unidades</option>
                <option value="tazas">Tazas</option>
                <option value="cucharadas">Cucharadas</option>
              </select>
            </div>
            <div class="form-field">
              <label>Tiempo de Preparación</label>
              <div class="time-input">
                <span class="material-symbols-rounded time-icon">schedule</span>
                <div class="segmented">
                  <select v-model="prepHour" class="time-select">
                    <option value>--</option>
                    <option v-for="h in hourOptions" :key="h" :value="h">{{ h }}</option>
                  </select>
                  <span class="colon">:</span>
                  <select v-model="prepMinute" class="time-select">
                    <option value>--</option>
                    <option v-for="m in minuteOptions" :key="m" :value="m">{{ m }}</option>
                  </select>
                </div>
                <button type="button" class="time-clear" v-if="prepHour || prepMinute"
                  @click="prepHour = ''; prepMinute = ''; updatePrepTime();">
                  <span class="material-symbols-rounded">close</span>
                </button>
              </div>
            </div>
          </div>
          <div class="form-field">
            <label>Notas</label>
            <textarea v-model="recipeForm.notes" class="input" rows="2"></textarea>
          </div>

          <h3 style="margin-top: 1rem;">Ingredientes</h3>
          
          <div class="form-row">
            <div class="form-field">
              <label>Ingrediente</label>
              <input v-model="ingredientForm.ingredient_name" type="text" class="input"
                placeholder="Nombre del ingrediente" />
            </div>
            <div class="form-field">
              <label>Cantidad</label>
              <input v-model.number="ingredientForm.quantity" type="number" step="0.01" class="input" />
            </div>
            <div class="form-field">
              <label>Unidad</label>
              <input v-model="ingredientForm.unit" type="text" class="input" placeholder="kg, litros, unidades" />
            </div>
            <div class="form-field" style="grid-column: 1 / -1;">
              <label>Notas</label>
              <textarea v-model="ingredientForm.notes" class="input" rows="1" placeholder="Opcional"></textarea>
            </div>
            <button class="btn-primary" @click="addIngredientToRecipe" style="align-self: end;">
              <span class="material-symbols-rounded">nutrition</span>
              Agregar
            </button>
          </div>

          <table class="data-table" style="margin-top: 1rem;" v-if="recipeIngredients.length > 0">
            <thead>
              <tr>
                <th>Producto</th>
              <th>Stock Actual</th>
              <th>Cantidad</th>
              <th>Unidad</th>
              <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(ing, idx) in recipeIngredients" :key="ing.id_recipe_ingredients || idx">
                <td>
                  <input v-model="ing.product_name" type="text" class="input" />
                </td>
                <td>
                  {{ getDisplayStock(ing) }}
                </td>
                <td>
                  <input v-model.number="ing.quantity" type="number" step="0.01" class="input" />
                </td>
                <td>
                  <input v-model="ing.unit" type="text" class="input" />
                </td>
                <td>
                  <div class="action-buttons">
                    <button class="btn-small btn-edit" v-if="ing.id_recipe_ingredients" @click="saveIngredientRow(ing)" title="Guardar">
                      <span class="material-symbols-rounded">save</span>
                    </button>
                    <button class="btn-small btn-delete" @click="removeIngredientFromRecipe(ing)">
                      <span class="material-symbols-rounded">delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="empty" style="padding: 1rem;">No hay ingredientes registrados</div>

        </div>
        <footer class="modal-footer">
          <button class="btn-secondary" @click="showRecipeModal = false">Cancelar</button>
          <button class="btn-primary" @click="saveRecipe">Guardar</button>
        </footer>
      </div>
    </div>

    <!-- INGREDIENTS MODAL -->
    <div v-if="showIngredientsModal" class="modal-overlay" @click.self="showIngredientsModal = false">
      <div class="modal modal-large">
        <header class="modal-header">
          <h2>Ingredientes: {{ selectedRecipe?.name }}</h2>
          <button class="btn-close" @click="showIngredientsModal = false">
            <span class="material-symbols-rounded">close</span>
          </button>
        </header>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-field">
              <label>Ingrediente</label>
              <input v-model="ingredientForm.ingredient_name" type="text" class="input"
                placeholder="Nombre del ingrediente" />
            </div>
            <div class="form-field">
              <label>Cantidad</label>
              <input v-model.number="ingredientForm.quantity" type="number" step="0.01" class="input" />
            </div>
            <div class="form-field">
              <label>Unidad</label>
              <input v-model="ingredientForm.unit" type="text" class="input" placeholder="kg, litros, unidades" />
            </div>
            <div class="form-field" style="grid-column: 1 / -1;">
              <label>Notas</label>
              <textarea v-model="ingredientForm.notes" class="input" rows="2" placeholder="Opcional"></textarea>
            </div>
            <button class="btn-primary" @click="addIngredient" style="align-self: end;">
              <span class="material-symbols-rounded">nutrition</span>
              Agregar
            </button>
          </div>

          <table class="data-table" style="margin-top: 1rem;">
            <thead>
              <tr>
                <th>Producto</th>
              <th>Stock Actual</th>
              <th>Cantidad</th>
              <th>Unidad</th>
              <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ing in recipeIngredients" :key="ing.id_recipe_ingredients">
                <td>
                  <input v-model="ing.product_name" class="input" />
                </td>
                <td>
                  {{ getDisplayStock(ing) }}
                </td>
                <td>
                  <input v-model.number="ing.quantity" type="number" step="0.01" class="input" />
                </td>
                <td>
                  <input v-model="ing.unit" class="input" />
                </td>
                <td>
                  <button class="btn-small btn-edit" @click="saveIngredientRow(ing)" title="Guardar">
                    <span class="material-symbols-rounded">save</span>
                  </button>
                  <button class="btn-delete" @click="removeIngredient(ing.id_recipe_ingredients)">
                    <span class="material-symbols-rounded">delete</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- USAGE MODAL -->
    <div v-if="showUsageModal" class="modal-overlay" @click.self="showUsageModal = false">
      <div class="modal modal-large">
        <header class="modal-header">
          <h2>Registrar Uso de Ingredientes</h2>
          <button class="btn-close" @click="showUsageModal = false">
            <span class="material-symbols-rounded">close</span>
          </button>
        </header>
        <div class="modal-body">
          <div class="form-field">
            <label>Receta (opcional)</label>
            <select v-model="usageForm.recipe_id" @change="onRecipeSelect" class="input">
              <option :value="null">Seleccionar...</option>
              <option v-for="r in recipes.filter(r => r.is_active)" :key="r.id_recipes" :value="r.id_recipes">
                {{ r.name }}
              </option>
            </select>
          </div>
          <!-- Porciones hidden as requested -->
          <div class="form-field" style="display:none;">
            <label>Porciones</label>
            <input v-model.number="usageForm.portions_made" type="number" min="1" class="input"
              @input="onRecipeSelect" />
          </div>
          <div class="form-field">
            <label>Notas</label>
            <textarea v-model="usageForm.notes" class="input" rows="2"></textarea>
          </div>

          <h3 style="margin-top: 1rem;">Ingredientes a Usar</h3>
          <table class="data-table">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Stock Actual</th>
                <th>Cantidad</th>
                <th>Unidad</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in usageForm.items" :key="idx">
                <td>{{ item.product_name }}</td>
                <td>{{ item.current_stock !== undefined ? (item.current_stock < 0 ? 0 : item.current_stock) : '-' }}</td>
                <td><input v-model.number="item.quantity" type="number" step="0.01" class="input" /></td>
                <td><input v-model="item.unit" type="text" class="input" /></td>
                <td>
                  <button class="btn-delete" @click="removeUsageItem(idx)">
                    <span class="material-symbols-rounded">delete</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <footer class="modal-footer">
          <button class="btn-secondary" @click="showUsageModal = false">Cancelar</button>
          <button class="btn-primary" @click="saveUsage" :disabled="!canSubmitUsage">
            Registrar Uso
          </button>
        </footer>
      </div>
    </div>

    <!-- IMAGE MODAL -->
    <div v-if="showImageModal" class="modal-overlay" @click.self="showImageModal = false">
      <div class="modal">
        <header class="modal-header">
          <h2>{{ isEditingImage ? "Editar" : "Capturar" }} Imagen</h2>
          <button class="btn-close" @click="showImageModal = false">
            <span class="material-symbols-rounded">close</span>
          </button>
        </header>
        <div class="modal-body">
          <div class="form-field">
            <label>Nombre</label>
            <input v-model="imageForm.name" type="text" class="input" />
          </div>
          <div class="form-field">
            <label>Descripción</label>
            <textarea v-model="imageForm.description" class="input" rows="2"></textarea>
          </div>
          <div class="form-field">
            <label>Receta (Opcional)</label>
            <select v-model="imageForm.recipe_id" class="input">
              <option :value="null">-- Sin receta --</option>
              <option v-for="r in recipes" :key="r.id_recipes" :value="r.id_recipes">
                {{ r.name }}
              </option>
            </select>
          </div>
          <div class="form-field">
            <label>Imagen {{ isEditingImage ? '(Opcional - sube nueva para reemplazar)' : '' }}</label>
            <input type="file" accept="image/*" @change="onImageSelect" class="input" />
          </div>
          <div v-if="imageForm.preview" class="image-preview-container">
            <img :src="imageForm.preview" alt="Preview" class="image-preview-large" @error="handleImageError($event)" />
          </div>
        </div>
        <footer class="modal-footer">
          <button class="btn-secondary" @click="showImageModal = false">Cancelar</button>
          <button class="btn-primary" @click="saveImage">{{ isEditingImage ? "Actualizar" : "Guardar" }}</button>
        </footer>
      </div>
    </div>

    <!-- Custom Notification Toast -->
    <transition name="toast">
      <div v-if="showNotification" :class="['toast-notification', notification.type]">
        <span class="material-symbols-rounded toast-icon">
          {{ notification.type === 'success' ? 'check_circle' : notification.type === 'error' ? 'error' : 'warning' }}
        </span>
        <span class="toast-message">{{ notification.message }}</span>
      </div>
    </transition>

    <!-- Custom Confirmation Modal -->
    <ConfirmModal
      :show="showConfirmModal"
      :title="confirmModal.title"
      :message="confirmModal.message"
      :html="confirmModal.html"
      :confirm-text="confirmModal.confirmText"
      :cancel-text="confirmModal.cancelText"
      :type="confirmModal.type"
      @confirm="confirmModal.onConfirm ? confirmModal.onConfirm() : null"
      @cancel="confirmModal.onCancel ? confirmModal.onCancel() : (showConfirmModal = false)"
    />

    <StatusModal
      :show="showStatusModal"
      :title="statusModal.title"
      :message="statusModal.message"
      :html="statusModal.html"
      :type="statusModal.type"
      @close="showStatusModal = false"
    />

    <ChatAssistant />
  </main>
</template>

<style scoped>
.wrap {
  padding: 1.5rem;
  display: grid;
  gap: 1.5rem;
  background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
  min-height: 100vh;
}

.top {
  display: grid;
  gap: 0.3rem;
}

.title {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--text);
}

.subtitle {
  color: var(--muted);
  font-size: 0.95rem;
}

/* Tabs */
.tabs {
  display: flex;
  gap: 0.5rem;
  border-bottom: 2px solid var(--border);
  padding-bottom: 0.5rem;
}

.tab {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  background: transparent;
  color: var(--muted);
  font-weight: 600;
  cursor: pointer;
  border-radius: 12px 12px 0 0;
  transition: all 0.2s ease;
}

.tab:hover {
  background: rgba(99, 102, 241, 0.1);
  color: #6366f1;
}

.tab-recipes.active {
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: white;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
}

.tab-usage.active {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.tab-images.active {
  background: linear-gradient(135deg, #0ea5e9, #0284c7);
  color: white;
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

/* Stats */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1rem;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  color: #fff;
  border: none;
  border-radius: 18px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
  min-height: 92px;
}

.stat-icon {
  font-size: 28px;
  background: rgba(255, 255, 255, 0.22);
  padding: 10px 12px;
  border-radius: 12px;
}

.stat-content {
  display: grid;
  gap: 6px;
}

.table-img-container {
  width: 50px;
  height: 50px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f1f5f9;
}

.table-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-img {
  color: var(--muted);
  font-size: 1rem;
}

.stat-label {
  font-size: 0.8rem;
  opacity: 0.9;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  font-weight: 700;
}

.stat-value {
  font-size: 1.6rem;
  font-weight: 800;
}

.stat-recipes {
  background: linear-gradient(135deg, #f59e0b, #f97316);
}

.stat-active {
  background: linear-gradient(135deg, #3b82f6, #4f46e5);
}

.stat-usage {
  background: linear-gradient(135deg, #10b981, #059669);
}

.stat-images {
  background: linear-gradient(135deg, #a855f7, #7c3aed);
}

/* Filters Bar */
.filters-bar {
  margin-top: 1.5rem;
  background: white;
  padding: 1rem;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
}

.filter-group {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  justify-content: flex-start;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  flex: 0 0 auto;
  min-width: 0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.filter-item:focus-within {
  background: white;
  border-color: rgba(198, 146, 61, 0.35);
  box-shadow: 0 4px 16px rgba(198, 146, 61, 0.2), 0 2px 8px rgba(0, 0, 0, 0.05);
}

.filter-icon {
  color: #94a3b8;
  font-size: 1.1rem;
}

.filter-input, .filter-select {
  border: none;
  background: transparent;
  width: 100%;
  font-size: 0.95rem;
  color: var(--text, #1e293b);
  outline: none;
  padding: 0;
  cursor: pointer;
}

.filter-input::placeholder {
  color: #94a3b8;
}

.search-item {
  flex: 2;
}

.date-input {
  font-family: inherit;
  color-scheme: light;
}

/* Tab Content */
.tab-content {
  display: grid;
  gap: 1rem;
}

.actions-bar {
  display: flex;
  justify-content: flex-end;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  transition: all 0.2s ease;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 0.75rem 1.5rem;
  background: #e5e7eb;
  color: #374151;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
}

/* Modern Buttons */
.action-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.btn-small {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 42px;
  height: 42px;
  padding: 0;
  background: #334155;
  color: #cbd5e1;
  border: none;
  border-radius: 10px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.btn-small:hover {
  color: white;
  transform: translateY(-2px);
}

/* Specific Button Colors */
.btn-view:hover {
  background: #3b82f6; /* Blue */
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-edit:hover,
.btn-small.btn-edit:hover {
  background: #f59e0b; /* Amber */
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.btn-delete {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 0;
  background: #334155;
  color: #cbd5e1;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.btn-delete:hover {
  background: #ef4444; /* Red */
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  color: white;
}

.btn-delete .material-symbols-rounded,
.btn-small .material-symbols-rounded {
  font-size: 20px;
}

/* Table */
.table-scroll {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.data-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  border: 1px solid #f1f5f9;
}

.data-table th {
  padding: 1rem;
  text-align: left;
  background: #f8fafc;
  color: #64748b;
  font-weight: 700;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e2e8f0;
}

.data-table td {
  padding: 1rem;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
}

.data-table tr:last-child td {
  border-bottom: none;
}

.data-table tr:hover {
  background: #f8fafc;
}

.badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
}

.badge-success {
  background: #d1fae5;
  color: #065f46;
}

.badge-inactive {
  background: #fee2e2;
  color: #991b1b;
}

/* Images Grid */
.images-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
}

.image-card {
  background: white;
  border: 1px solid rgba(0,0,0,0.05);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  position: relative;
}

.image-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border-color: rgba(99, 102, 241, 0.2);
}

.image-card-header {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  background-color: #f1f5f9;
}

.image-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.image-card:hover .image-preview {
  transform: scale(1.05);
}

.image-recipe-badge {
  position: absolute;
  bottom: 0.75rem;
  left: 0.75rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  color: #059669;
  border-radius: 99px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  max-width: calc(100% - 1.5rem);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.image-recipe-badge .material-symbols-rounded {
  font-size: 16px;
}

.image-info {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.image-name {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.4;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.image-desc {
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.5;
  margin-bottom: auto;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.image-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  justify-content: flex-end;
}

.btn-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-icon:hover {
  filter: brightness(0.95);
}

.btn-icon.btn-edit {
  background: #eff6ff;
  color: #3b82f6;
}

.btn-icon.btn-edit:hover {
  background: #dbeafe;
  color: #DFC05E;
}

.btn-icon.btn-delete {
  background: #fef2f2;
  color: #ef4444;
}

.btn-icon.btn-delete:hover {
  background: #fee2e2;
  color: #dc2626;
}

.btn-icon .material-symbols-rounded {
  font-size: 20px;
}

/* Pagination */
.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
  color: #64748b;
  font-weight: 500;
}

.btn-pagination {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: white;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
}

.btn-pagination:hover:not(:disabled) {
  background: #f8fafc;
  color: #1e293b;
  border-color: #cbd5e1;
}

.btn-pagination:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(8px);
  padding: 1rem;
}

.modal {
  background: white;
  border-radius: 24px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.5);
  animation: modalPop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  display: flex;
  flex-direction: column;
}

@keyframes modalPop {
  from { opacity: 0; transform: scale(0.95) translateY(10px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}

.modal-large {
  max-width: 900px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #f1f5f9;
  background: white;
  position: sticky;
  top: 0;
  z-index: 10;
}

.modal-header h2 {
  font-size: 1.5rem;
  font-weight: 800;
  color: #1e293b;
  letter-spacing: -0.025em;
}

.btn-close {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-close:hover {
  background: #f1f5f9;
  color: #ef4444;
  transform: rotate(90deg);
}

.modal-body {
  padding: 2rem;
  display: grid;
  gap: 1.5rem;
  overflow-y: auto;
}

.modal-footer {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding: 1.5rem 2rem;
  border-top: 1px solid #f1f5f9;
  background: #f8fafc;
  position: sticky;
  bottom: 0;
  z-index: 10;
}

.form-field {
  display: grid;
  gap: 0.5rem;
}

.form-field label {
  font-weight: 600;
  color: var(--text);
  font-size: 0.9rem;
}

.label-with-button {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.btn-ai-suggest {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.3);
}

.btn-ai-suggest:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
}

.btn-ai-suggest:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-ai-suggest .material-symbols-rounded {
  font-size: 18px;
  animation: spin 2s linear infinite;
}

.btn-ai-suggest:not(:disabled) .material-symbols-rounded {
  animation: none;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.input {
  padding: 0.875rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.95rem;
  color: #1e293b;
  background: #f8fafc;
  transition: all 0.2s ease;
}

.input:focus {
  outline: none;
  background: white;
  border-color: #6366f1;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
}

.time-input {
  position: relative;
  display: flex;
  align-items: center;
}

.segmented {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding-left: 44px;
  padding-right: 44px;
}

.time-icon {
  position: absolute;
  left: 12px;
  font-size: 20px;
  color: #6366f1;
  background: rgba(99, 102, 241, 0.12);
  border-radius: 10px;
  padding: 6px;
}

.time-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 0.6rem 0.9rem;
  font-weight: 700;
  color: #111827;
  background: linear-gradient(135deg, #f3f4f6, #ffffff);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.time-select:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}

.colon {
  font-weight: 800;
  color: #6b7280;
}

.time-clear {
  position: absolute;
  right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: #e5e7eb;
  color: #374151;
  cursor: pointer;
}

.time-clear:hover {
  background: #d1d5db;
}

.time-control::-webkit-inner-spin-button,
.time-control::-webkit-clear-button {
  display: none;
}

.time-control::-webkit-calendar-picker-indicator {
  opacity: 0.7;
  filter: hue-rotate(250deg) saturate(1.4);
}

.alert {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 12px;
}

.alert-warning {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fbbf24;
}

.image-preview-container {
  margin-top: 1rem;
}

.image-preview-large {
  width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.status-message-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  color: #047857;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  border: 1px solid #a7f3d0;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.1);
}

.status-icon {
  font-size: 1.2rem;
  color: #10b981;
}

.status-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}

.loading,
.empty {
  text-align: center;
  padding: 3rem;
  color: var(--muted);
  font-size: 1.1rem;
}

/* Toast Notification */
.toast-notification {
  position: fixed;
  top: 2rem;
  right: 2rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  z-index: 10000;
  font-weight: 600;
  min-width: 300px;
  backdrop-filter: blur(10px);
}

.toast-notification.success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.toast-notification.error {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.toast-notification.warning {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
}

.toast-icon {
  font-size: 24px;
}

.toast-message {
  flex: 1;
}

/* Toast Animation */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.toast-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

/* Confirmation Modal */
.confirm-modal {
  max-width: 500px;
}

.confirm-message {
  font-size: 1rem;
  line-height: 1.6;
  color: var(--text);
}

.confirm-modal .modal-icon {
  color: #f59e0b;
}

.btn-clear-filters {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  color: #64748b;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.2s;
  margin-left: auto;
}

.btn-clear-filters:hover {
  background: #e2e8f0;
  color: #334155;
  border-color: #94a3b8;
}

.btn-clear-filters:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.btn-clear-filters .material-symbols-rounded {
  font-size: 1.1rem;
}

/* Dark Mode */
@media (prefers-color-scheme: dark) {
  .btn-clear-filters {
    background: #1e293b;
    border-color: #475569;
    color: #94a3b8;
  }
  
  .btn-clear-filters:hover {
    background: #334155;
    color: #f1f5f9;
    border-color: #64748b;
  }

  .image-card,
  .modal {
    background: #1e293b;
    border-color: #334155;
  }

  .data-table {
    background: #1e293b;
    border: none;
  }

  .modal-header,
  .modal-footer {
    background: #1e293b;
    border-color: #334155;
  }

  .modal-header h2 {
    color: #f1f5f9;
  }

  .btn-close {
    background: #334155;
    border-color: #475569;
    color: #cbd5e1;
  }

  .btn-close:hover {
    background: #475569;
    color: #fff;
  }

  .data-table th {
    background: #0f172a;
    color: #e2e8f0;
    border-color: #334155;
  }

  .data-table td {
    border-color: #334155;
    color: #cbd5e1;
  }

  .data-table tr:hover {
    background: #334155;
  }

  .input {
    background: #0f172a;
    border-color: #334155;
    color: #e2e8f0;
  }

  .input:focus {
    background: #1e293b;
  }

  .btn-secondary {
    background: #334155;
    color: #e2e8f0;
  }

  .modal-header {
    border-color: #334155;
  }

  .modal-footer {
    border-color: #334155;
  }

  .btn-small {
    background: #334155;
    color: #cbd5e1;
  }

  .btn-small:hover {
    background: #475569;
    color: #f1f5f9;
  }

  .btn-edit:hover {
    background: #f59e0b;
    color: white;
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  }

  .btn-delete:hover {
    background: #ef4444;
    color: white;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  }

  /* Timer Styles */
  .timer-cell {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-family: 'Monaco', 'Consolas', monospace;
  }

  .timer-display {
    min-width: 60px;
    font-weight: 600;
    color: #475569;
  }

  .timer-display.active {
    color: #e11d48; /* Red for active countdown */
    font-weight: 700;
  }

  .status-message-text {
    font-size: 0.85rem;
    color: #059669; /* Green-600 */
    font-weight: 600;
    line-height: 1.2;
    display: block;
    max-width: 200px;
  }

  .btn-icon-timer {
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    display: flex;
    align-items: center;
    border-radius: 50%;
    transition: all 0.2s;
  }

  .btn-icon-timer.start {
    color: #10b981; /* Green */
  }

  .btn-icon-timer.start:hover {
    background: #d1fae5;
  }

  .btn-icon-timer.stop {
    color: #ef4444; /* Red */
  }

  .btn-icon-timer.stop:hover {
    background: #fee2e2;
  }

  .image-card-header {
    background-color: #0f172a;
  }
  
  .image-name {
    color: #f1f5f9;
  }
  
  .image-desc {
    color: #94a3b8;
  }
  
  .btn-icon {
    background: #334155;
    color: #cbd5e1;
  }

  .btn-icon.btn-edit {
    background: rgba(59, 130, 246, 0.2); /* Blue tint */
    color: #60a5fa;
  }
  
  .btn-icon.btn-delete {
    background: rgba(239, 68, 68, 0.2); /* Red tint */
    color: #f87171;
  }
  
  .btn-icon:hover {
    background: #475569;
    color: #f1f5f9;
  }
  
  .btn-icon.btn-edit:hover {
    background: rgba(59, 130, 246, 0.3);
    color: #93c5fd;
  }

  .btn-icon.btn-delete:hover {
    background: rgba(239, 68, 68, 0.3);
    color: #fca5a5;
  }

  .pagination-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid #334155;
    color: #cbd5e1;
  }

  .btn-pagination {
    background: #334155;
    color: #cbd5e1;
  }

  .btn-pagination:hover:not(:disabled) {
    background: #475569;
    color: #f1f5f9;
  }

  .btn-pagination:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Dark Mode Filters */
  .filters-bar {
    background: #1e293b;
    border-color: #334155;
  }

  .filter-item {
    background: #0f172a;
    border-color: #334155;
  }

  .filter-item:focus-within {
    background: #0f172a;
    border-color: #6366f1;
  }

  .filter-input, .filter-select {
    color: #f1f5f9;
    background-color: transparent;
  }

  .filter-select option {
    background-color: #1e293b;
    color: #f1f5f9;
  }

  .filter-input::placeholder {
    color: #64748b;
  }

  .filter-icon {
    color: #94a3b8;
  }

  .date-input {
    color-scheme: dark;
  }

  /* Nav Search Dark Mode */
  .nav-input {
    background: #0f172a;
    border-color: #334155;
    color: #f1f5f9;
  }

  .nav-input:focus {
    background: #1e293b;
    border-color: #6366f1;
    box-shadow: 0 4px 16px rgba(99, 102, 241, 0.25), 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  .nav-search .search-icon {
    color: #94a3b8;
  }

  .nav-input:focus ~ .search-icon {
    color: #818cf8;
  }

  .nav-search .clear-btn {
    color: #94a3b8;
  }

  .nav-search .clear-btn:hover {
    color: #f1f5f9;
    background: rgba(99, 102, 241, 0.2);
  }

  /* Suggestions Dropdown Dark Mode */
  .nav-suggestions {
    background: #1e293b;
    border-color: #334155;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4), 0 4px 16px rgba(0, 0, 0, 0.2);
  }

  .nav-suggestions .suggestion-item {
    color: #e2e8f0;
  }

  .nav-suggestions .suggestion-item.active,
  .nav-suggestions .suggestion-item:hover {
    background: rgba(99, 102, 241, 0.15);
    border-color: rgba(99, 102, 241, 0.3);
  }

  .nav-suggestions .suggestion-thumb {
    border-color: #334155;
    background: #0f172a;
  }

  .nav-suggestions .placeholder-thumb {
    background: #0f172a;
    color: #64748b;
  }

  .nav-suggestions .suggestion-meta {
    color: #94a3b8;
  }
}

/* Dark Mode Manual Toggle Support */
:root[data-theme="dark"] {
  .image-card,
  .modal {
    background: #1e293b;
    border-color: #334155;
  }
  
  /* ... existing dark mode styles ... */


  .data-table {
    background: #1e293b;
    border: none;
  }

  .modal-header,
  .modal-footer {
    background: #1e293b;
    border-color: #334155;
  }

  .modal-header h2 {
    color: #f1f5f9;
  }

  .btn-close {
    background: #334155;
    border-color: #475569;
    color: #cbd5e1;
  }

  .btn-close:hover {
    background: #475569;
    color: #fff;
  }

  .data-table th {
    background: #0f172a;
    color: #e2e8f0;
    border-color: #334155;
  }

  .data-table td {
    border-color: #334155;
    color: #cbd5e1;
  }

  .data-table tr:hover {
    background: #334155;
  }

  .input {
    background: #0f172a;
    border-color: #334155;
    color: #e2e8f0;
  }

  .input:focus {
    background: #1e293b;
  }

  .btn-secondary {
    background: #334155;
    color: #e2e8f0;
  }

  .modal-header {
    border-color: #334155;
  }

  .modal-footer {
    border-color: #334155;
  }

  .btn-small {
    background: #334155;
    color: #cbd5e1;
  }

  .btn-small:hover {
    background: #475569;
    color: #f1f5f9;
  }

  .btn-edit:hover {
    background: #f59e0b;
    color: white;
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  }

  .btn-delete:hover {
    background: #ef4444;
    color: white;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  }

  /* Timer Styles */
  .timer-cell {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-family: 'Monaco', 'Consolas', monospace;
  }

  .timer-display {
    min-width: 60px;
    font-weight: 600;
    color: #475569;
  }

  .timer-display.active {
    color: #e11d48; /* Red for active countdown */
    font-weight: 700;
  }

  .status-message-text {
    font-size: 0.85rem;
    color: #059669; /* Green-600 */
    font-weight: 600;
    line-height: 1.2;
    display: block;
    max-width: 200px;
  }

  .btn-icon-timer {
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    display: flex;
    align-items: center;
    border-radius: 50%;
    transition: all 0.2s;
  }

  .btn-icon-timer.start {
    color: #10b981; /* Green */
  }

  .btn-icon-timer.start:hover {
    background: #d1fae5;
  }

  .btn-icon-timer.stop {
    color: #ef4444; /* Red */
  }

  .btn-icon-timer.stop:hover {
    background: #fee2e2;
  }

  /* Dark Mode Filters */
  .filters-bar {
    background: #1e293b;
    border-color: #334155;
  }

  .filter-item {
    background: #0f172a;
    border-color: #334155;
  }

  .filter-item:focus-within {
    background: #0f172a;
    border-color: #6366f1;
  }

  .filter-input, .filter-select {
    color: #f1f5f9;
    background-color: transparent;
  }

  .filter-select option {
    background-color: #1e293b;
    color: #f1f5f9;
  }

  .filter-input::placeholder {
    color: #64748b;
  }

  .filter-icon {
    color: #94a3b8;
  }

  .date-input {
    color-scheme: dark;
  }

  .btn-clear-filters {
    background: #1e293b;
    border-color: #475569;
    color: #94a3b8;
  }
  
  .btn-clear-filters:hover:not(:disabled) {
    background: #334155;
    color: #f1f5f9;
    border-color: #64748b;
  }

  .btn-clear-filters:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Pagination */
  .pagination-controls {
    border-top-color: #334155;
    color: #cbd5e1;
  }

  .btn-pagination {
    background: #334155;
    color: #cbd5e1;
    border-color: #475569;
  }

  .btn-pagination:hover:not(:disabled) {
    background: #475569;
    color: #f1f5f9;
    border-color: #64748b;
  }

  .btn-pagination:disabled {
    opacity: 0.5;
  }

  /* Nav Search Dark Mode */
  .nav-input {
    background: #0f172a;
    border-color: #334155;
    color: #f1f5f9;
  }

  .nav-input:focus {
    background: #1e293b;
    border-color: #6366f1;
    box-shadow: 0 4px 16px rgba(99, 102, 241, 0.25), 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  .nav-search .search-icon {
    color: #94a3b8;
  }

  .nav-input:focus ~ .search-icon {
    color: #818cf8;
  }

  .nav-search .clear-btn {
    color: #94a3b8;
  }

  .nav-search .clear-btn:hover {
    color: #f1f5f9;
    background: rgba(99, 102, 241, 0.2);
  }

  /* Suggestions Dropdown Dark Mode */
  .nav-suggestions {
    background: #1e293b;
    border-color: #334155;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4), 0 4px 16px rgba(0, 0, 0, 0.2);
  }

  .nav-suggestions .suggestion-item {
    color: #e2e8f0;
  }

  .nav-suggestions .suggestion-item.active,
  .nav-suggestions .suggestion-item:hover {
    background: rgba(99, 102, 241, 0.15);
    border-color: rgba(99, 102, 241, 0.3);
  }

  .nav-suggestions .suggestion-thumb {
    border-color: #334155;
    background: #0f172a;
  }

  .nav-suggestions .placeholder-thumb {
    background: #0f172a;
    color: #64748b;
  }

  .nav-suggestions .suggestion-meta {
    color: #94a3b8;
  }
}

/* Force Light Mode (overrides system dark mode if manually selected) */
:root[data-theme="light"] {
  .image-card,
  .modal {
    background: white;
    border-color: #f1f5f9;
  }

  .data-table {
    background: white;
    border: 1px solid #f1f5f9;
  }

  .modal-header,
  .modal-footer {
    background: white;
    border-color: #f1f5f9;
  }
  
  .modal-footer {
    background: #f8fafc;
  }

  .modal-header h2 {
    color: #1e293b;
  }

  .btn-close {
    background: #f8fafc;
    border-color: #e2e8f0;
    color: #64748b;
  }

  .btn-close:hover {
    background: #eff6ff;
    color: #3b82f6;
  }

  .data-table th {
    background: #f8fafc;
    color: #64748b;
    border-color: #e2e8f0;
  }

  .data-table td {
    border-color: #f1f5f9;
    color: #334155;
  }

  .data-table tr:hover {
    background: #f8fafc;
  }

  .input {
    background: #f8fafc;
    border-color: #e2e8f0;
    color: #1e293b;
  }

  .input:focus {
    background: white;
  }

  .btn-secondary {
    background: #e5e7eb;
    color: #374151;
  }

  /* Filters Light Mode Override */
  .filters-bar {
    background: white;
    border-color: #e2e8f0;
  }

  .filter-item {
    background: #f8fafc;
    border-color: #e2e8f0;
  }

  .filter-item:focus-within {
    background: white;
    border-color: #6366f1;
  }

  .filter-input, .filter-select {
    color: #1e293b;
    background-color: #f8fafc;
  }

  .filter-select option {
    background-color: white;
    color: #1e293b;
  }

  .filter-input::placeholder {
    color: #94a3b8;
  }

  .filter-icon {
    color: #64748b;
  }

  .btn-clear-filters {
    background: #f1f5f9;
    border-color: #cbd5e1;
    color: #64748b;
  }

  .btn-clear-filters:hover {
    background: #e2e8f0;
    color: #334155;
    border-color: #94a3b8;
  }
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem;
}

.pagination-controls span {
  font-weight: 600;
  color: var(--text);
  font-size: 1.1rem;
}

.btn-pagination {
  background-color: #3b82f6;
  color: white;
  border: none;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.btn-pagination:hover:not(:disabled) {
  background-color: #DFC05E;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.3);
}

.btn-pagination:disabled {
  background-color: #e2e8f0;
  color: #94a3b8;
  cursor: not-allowed;
  box-shadow: none;
}

/* Nav Search */
.nav-search {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  width: 100%;
}

.nav-input {
  width: 100%;
  padding: 0.55rem 2.2rem 0.55rem 2.5rem;
  border-radius: 12px;
  border: 1px solid rgba(198, 146, 61, 0.15);
  background: white;
  color: var(--text, #1e293b);
  font-size: 0.95rem;
  outline: none;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.nav-input:focus {
  background: white;
  box-shadow: 0 4px 16px rgba(198, 146, 61, 0.2), 0 2px 8px rgba(0, 0, 0, 0.05);
  border-color: rgba(198, 146, 61, 0.35);
}

.nav-search .search-icon {
  position: absolute;
  left: 12px;
  pointer-events: none;
  font-size: 1.1rem;
  color: #94a3b8;
  transition: color 0.25s ease;
}

.nav-input:focus~.search-icon {
  color: #6366f1;
}

.nav-search .clear-btn {
  position: absolute;
  right: 8px;
  border: none;
  background: transparent;
  color: #94a3b8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  padding: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.nav-search .clear-btn:hover {
  color: #1e293b;
  background: rgba(198, 146, 61, 0.1);
  transform: scale(1.1);
}

/* Suggestions Dropdown */
.nav-suggestions {
  position: absolute;
  left: 0;
  top: calc(100% + 8px);
  width: min(420px, 70vw);
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 0, 0, 0.1);
  padding: 0.4rem;
  z-index: 1000;
  animation: dropdownFadeIn 0.2s ease-out;
}

@keyframes dropdownFadeIn {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}

.nav-suggestions .suggestion-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.6rem;
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.nav-suggestions .suggestion-item.active,
.nav-suggestions .suggestion-item:hover {
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.12) 0%, rgba(0, 194, 168, 0.08) 100%);
  border-color: rgba(198, 146, 61, 0.25);
  transform: translateX(2px);
}

.nav-suggestions .suggestion-thumb {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #f1f5f9;
}

.nav-suggestions .placeholder-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  color: #cbd5e1;
}

.nav-suggestions .suggestion-info {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.nav-suggestions .suggestion-name {
  font-weight: 600;
  font-size: 0.95rem;
}

.nav-suggestions .suggestion-meta {
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 500;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .wrap {
    padding: 1rem;
    gap: 1rem;
  }

  .title {
    font-size: 1.25rem;
  }

  .subtitle {
    font-size: 0.85rem;
  }

  /* Tabs Stacked/Wrapped on Mobile */
  .tabs {
    flex-wrap: wrap;
    overflow-x: visible;
    white-space: normal;
    padding-bottom: 0;
    gap: 0.75rem;
    border-bottom: none;
  }

  .tab {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
    flex: 1 1 auto; /* Grow to fill space */
    width: auto;
    display: flex;
    justify-content: center;
    border-radius: 12px; /* Pill shape */
    background-color: #f8fafc; /* Light bg for inactive */
    border: 1px solid #e2e8f0;
  }

  .tab.active {
    border-color: transparent;
  }

  /* Stats Grid: Stack on mobile */
  .stats-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .stat-card {
    padding: 1rem;
    min-height: auto;
  }

  /* Tables */
  .table-scroll {
    margin: 0 -1rem; /* Full width on mobile */
    border-radius: 0;
    border-left: none;
    border-right: none;
    width: calc(100% + 2rem);
  }

  .data-table th,
  .data-table td {
    padding: 0.75rem 0.5rem;
    font-size: 0.85rem;
  }

  .table-img-container {
    width: 40px;
    height: 40px;
  }

  /* Modals */
  .modal {
    width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
    display: flex;
    flex-direction: column;
  }
  
  .modal-overlay {
    padding: 0;
    align-items: flex-end; /* Sheet-like on some mobiles, or full screen */
  }

  .modal-header {
    padding: 1rem;
  }

  .modal-body {
    padding: 1rem;
    flex: 1;
    overflow-y: auto;
  }

  .modal-footer {
    padding: 1rem;
    flex-shrink: 0; /* Ensure footer stays visible or at bottom */
  }

  /* Forms */
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .btn-primary, .btn-secondary {
    padding: 0.7rem 1rem;
    font-size: 0.9rem;
    width: 100%; /* Full width buttons on mobile */
    justify-content: center;
  }

  .modal-footer {
    flex-direction: column-reverse; /* Stack buttons, primary on top usually or reverse */
    gap: 0.75rem;
  }
  
  .modal-footer button {
    width: 100%;
    margin: 0;
  }

  /* Images Grid */
  .images-grid {
    grid-template-columns: 1fr; /* Single column for images */
  }
  
  /* Pagination */
  .pagination-controls {
    flex-direction: row;
    padding: 0.5rem;
    gap: 0.5rem;
  }
  
  .pagination-controls span {
    font-size: 0.9rem;
  }
  
  .btn-pagination {
    width: 40px;
    height: 40px;
  }

  /* Toast Mobile */
  .toast-notification {
    top: 1rem;
    right: 1rem;
    left: 1rem;
    min-width: auto;
    width: auto;
    justify-content: flex-start;
  }

  /* Prevent iOS Zoom on inputs */
  .input,
  .time-select,
  select {
    font-size: 16px !important;
  }

  /* Adjust Time Input for small screens */
  .segmented {
    padding-left: 38px;
    padding-right: 38px;
  }

  .time-icon {
    left: 8px;
  }

  .time-clear {
    right: 4px;
  }
}
</style>
